import { BookingService } from '../../services/DataService';
import {
  Booking,
  BookingFormData,
  BlockedSlot,
  Resource,
  StaffMember,
  Schedule,
  CalendarEvent,
  AgendaItem
} from '../../types/bookings';
import { callAdminApi } from './wixAdminApi';
import { ServiceType } from '../../types/services';
import { ServiceTypesApi } from './serviceTypes';
import { DataStorageService } from '../DataStorageService';

// Type for resource/staff metadata stored on bookings
type BookingWithMetadata = Booking & {
  _resourceMetadata?: {
    id: string;
    type: string;
    staffMemberId: string;
  };
};

function parseWixDate(dateStr: string | undefined): Date | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

// Legacy shape callers of queryAvailability() already expect — kept stable so
// the admin-override/exact-slot-matching logic below doesn't need to change.
interface LegacyAvailabilityEntry {
  slot?: {
    serviceId?: string;
    scheduleId?: string;
    startDate?: string;
    endDate?: string;
    resource?: { name?: string; _id?: string };
    location?: { locationType?: string; formattedAddress?: string };
  };
  bookable?: boolean;
}

// Wix Bookings' queryAvailability (Availability Calendar) is deprecated in favor
// of listAvailabilityTimeSlots (Availability Time Slots). The new API's response
// is flat (localStartDate/localEndDate directly on the slot, no nested `slot`
// object) and uses local-time strings instead of ISO-with-offset. This wraps the
// new call and translates its response back into the old nested shape so none of
// the booking-creation logic below (admin override, exact-time matching, etc.)
// needs to change.
async function queryAvailabilityTimeSlots(query: {
  filter: { serviceId: string; startDate: string; endDate: string };
  timezone: string;
  bookable?: boolean;
}): Promise<{ availabilityEntries: LegacyAvailabilityEntry[] }> {
  // startDate/endDate arrive as full ISO strings (with Z/offset) from the
  // callers below; listAvailabilityTimeSlots wants local wall-clock time
  // (YYYY-MM-DDThh:mm:ss, no offset) in the timezone it's given.
  const toLocalDateTime = (iso: string): string => iso.replace(/\.\d{3}Z$/, '').replace(/Z$/, '');

  const result = await callAdminApi('queryAvailability', 'bookings', {
    serviceId: query.filter.serviceId,
    fromLocalDate: toLocalDateTime(query.filter.startDate),
    toLocalDate: toLocalDateTime(query.filter.endDate),
    timeZone: query.timezone,
    ...(query.bookable !== undefined && { bookable: query.bookable }),
  }) as {
    timeSlots?: Array<{
      serviceId?: string;
      scheduleId?: string;
      localStartDate?: string;
      localEndDate?: string;
      bookable?: boolean;
      location?: { locationType?: string; formattedAddress?: string };
      availableResources?: Array<{ resources?: Array<{ name?: string; id?: string }> }>;
    }>;
  };

  const timeSlots = result?.timeSlots || [];

  const availabilityEntries: LegacyAvailabilityEntry[] = timeSlots.map(ts => {
    const firstResource = ts.availableResources?.[0]?.resources?.[0];
    return {
      slot: {
        serviceId: ts.serviceId,
        scheduleId: ts.scheduleId,
        startDate: ts.localStartDate,
        endDate: ts.localEndDate,
        ...(firstResource && { resource: { name: firstResource.name, _id: firstResource.id } }),
        ...(ts.location && { location: ts.location }),
      },
      bookable: ts.bookable,
    };
  });

  return { availabilityEntries };
}

// Same conversion as ServiceTypesApi.convertWixImageUrl (private there) —
// Wix's internal wix:image:// URIs aren't directly usable as a plain image
// URL (e.g. for an eCommerce order line item's `image` field).
function convertWixImageUrl(wixUrl: string): string {
  if (!wixUrl || !wixUrl.startsWith('wix:image://')) {
    return wixUrl;
  }
  const match = wixUrl.match(/^wix:image:\/\/v1\/([^\/?#]+)/);
  let staticFileName = match ? match[1] : '';
  if (!staticFileName) {
    const fallbackMatch = wixUrl.match(/^wix:image:\/\/v1\/([^?#]+)/);
    staticFileName = fallbackMatch ? fallbackMatch[1] : '';
  }
  staticFileName = staticFileName.split('?')[0].split('#')[0];
  if (/\.(jpg|jpeg|png|gif|webp)$/i.test(staticFileName)) {
    return `https://static.wixstatic.com/media/${staticFileName}`;
  }
  return `https://static.wixstatic.com/media/${staticFileName}.jpg`;
}

// Wix SDK can return dates as Date objects, ISO strings, or { $date: ms } objects
function parseWixDateField(raw: unknown): Date {
  if (!raw) return new Date();
  if (raw instanceof Date) return isNaN(raw.getTime()) ? new Date() : raw;
  if (typeof raw === 'number') return new Date(raw);
  if (typeof raw === 'object' && raw !== null && '$date' in raw) {
    const ts = (raw as { $date: number | string }).$date;
    return new Date(typeof ts === 'string' ? parseInt(ts, 10) : ts);
  }
  if (typeof raw === 'string') {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? new Date() : d;
  }
  return new Date();
}

function mapWixBookingToBooking(raw: unknown): Booking {
  const bookingObj = (typeof raw === 'object' && raw !== null && 'booking' in raw)
    ? (raw as { booking: Record<string, unknown> }).booking
    : raw;
  const booking = bookingObj as Record<string, unknown>;
  const contact = (booking.contactDetails as Record<string, unknown>) || {};


  
  const bookedEntity = (booking.bookedEntity as Record<string, unknown>) || {};
  const slot = (bookedEntity.slot as Record<string, unknown>) || {};
  

  
  // Address
  const locationObj = (slot.location as Record<string, unknown>) || {};
  const address = (locationObj.formattedAddress as string) || '';
  
  // Resource (team) and Staff extraction from Wix data
  // Wix structure:
  // - slot.resource = the PRIMARY assigned person (staff member doing the work)
  // - slot.additionalResources[0] = the TEAM RESOURCE slot (Nicole, Team Member 01, etc.)
  const primaryResourceObj = (slot.resource as Record<string, unknown>) || {};
  const staffId = (primaryResourceObj._id as string) || '';
  const staffName = (primaryResourceObj.name as string) || '';
  
  // Get team resource from additionalResources array
  const additionalResourcesArray = (slot.additionalResources as Array<Record<string, unknown>>) || [];
  const teamResourceObj = additionalResourcesArray.length > 0 ? additionalResourcesArray[0] : {};
  const resourceId = (teamResourceObj._id as string) || '';
  const resourceName = (teamResourceObj.name as string) || '';
  const resourceTypeId = (teamResourceObj.type as string) || '';
  
  // Parse additionalFields - Wix stores these as array of {value, valueType, _id}
  // NO keys - just values in order they were added
  const additionalFields = (booking.additionalFields as Array<Record<string, unknown>>) || [];
  
  // For backward compatibility, check if any fields have keys (old format)
  const additionalFieldsMap = new Map<string, string>();
  additionalFields.forEach(field => {
    const key = field.key as string;
    const value = field.value as string;
    if (key && value) {
      additionalFieldsMap.set(key, value);
    }
  });
  
  // Read resource and staff from additionalFields (your app's custom tracking)
  // These override what Wix stores in slot.resource
  // If additionalFields are empty, use the values from Wix slot structure
  let resource = additionalFieldsMap.get('resource') || resourceName;  // Team slot (Nicole, Team Member 01, etc.)
  let staff = additionalFieldsMap.get('staff') || staffName;            // Staff member (Raquel Slater, etc.)
  
  // Store metadata for API lookups (not used if additionalFields have values)
  const _resourceMetadata = { id: resourceId, type: resourceTypeId, staffMemberId: staffId };
  
  // Robust start/end time extraction
  const startTimeStr = (slot.startDate as string) || (booking.startDate as string) || (bookedEntity.startDate as string) || '';
  const endTimeStr = (slot.endDate as string) || (booking.endDate as string) || (bookedEntity.endDate as string) || '';
  const startTime = parseWixDate(startTimeStr) || new Date();
  const endTime = parseWixDate(endTimeStr) || new Date();

  // IMPORTANT: Extract the date in LOCAL time zone, not UTC
  // The date string from Wix is in UTC, but we need the local date for display
  const localYear = startTime.getFullYear();
  const localMonth = String(startTime.getMonth() + 1).padStart(2, '0');
  const localDay = String(startTime.getDate()).padStart(2, '0');
  const localDateStr = `${localYear}-${localMonth}-${localDay}`;
  const id = (booking._id as string) || (booking.id as string) || '';
  const title = (bookedEntity.title as string) || (booking.title as string) || '';
  const serviceType = (bookedEntity.title as string) || (booking.serviceType as string) || 'Other';
  
  // Robust contact id extraction: prefer contactId, then id, then _id
  const contactId = (contact.contactId as string) || (contact.id as string) || (contact._id as string) || '';
  
  // For avatar, we need to fetch from contact data since Wix booking doesn't include it
  // This will be handled by enriching bookings with contact data after fetching
  const avatarUrl = '';
  
  // Parse additional info from additionalFields
  const notes = additionalFieldsMap.get('notes') || (booking.notes as string) || '';
  const price = parseFloat(additionalFieldsMap.get('price') || '0') || 0;
  const duration = parseInt(additionalFieldsMap.get('duration') || '0') || 0;
  const location = additionalFieldsMap.get('location') || address;
  
  return {
    id,
    title,
    type: 'booking' as const, // Ensure real bookings have the correct type
    startTime,
    endTime,
    date: localDateStr, // Use local date, not UTC date
    serviceType: serviceType as ServiceType,
    status: (() => {
      const rawStatus = booking.status as string;
      const mappedStatus = (rawStatus as 'pending' | 'confirmed' | 'completed' | 'cancelled') || 'pending';
      return mappedStatus;
    })(),
    notes,
    location,
    contactId: contactId,
    contact: {
      id: contactId,
      name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
      email: (contact.email as string) || '',
      phone: (contact.phone as string) || '',
      address: address,
      avatarUrl: avatarUrl,
    },
    staff,
    resource,
    price,
    isCompleted: false,
    duration,
    frequency: 'one-time' as const,
    bookingId: (booking.bookingId as string) || '',
    createdAt: parseWixDateField(booking._createdDate),
    updatedAt: parseWixDateField(booking._updatedDate),
    _resourceMetadata,
  } as BookingWithMetadata;
}

// Function to enrich bookings with contact avatars - single bulk query
async function enrichBookingsWithContactAvatars(bookings: Booking[]): Promise<Booking[]> {
  if (!bookings.length) return bookings;

  try {
    const contactIds = [...new Set(bookings.map(b => b.contact.id).filter(Boolean))];

    if (!contactIds.length) return bookings;

    // Single query fetching all contacts at once using $in filter
    const response = await callAdminApi('queryContacts', 'contacts', {
      filter: { _id: { $in: contactIds } },
      limit: contactIds.length
    }) as { _items?: Record<string, unknown>[] };

    const avatarMap = new Map<string, string>();
    (response._items ?? []).forEach((contact) => {
      const id = contact._id as string;
      const picture = contact.picture;
      let avatarUrl = '';
      if (typeof picture === 'string') {
        avatarUrl = picture;
      } else if (picture && typeof picture === 'object') {
        const p = picture as Record<string, unknown>;
        avatarUrl = String(p.image ?? p.url ?? '');
      }
      if (id && avatarUrl) avatarMap.set(id, avatarUrl);
    });

    return bookings.map(booking => ({
      ...booking,
      contact: {
        ...booking.contact,
        avatarUrl: avatarMap.get(booking.contact.id) || booking.contact.avatarUrl
      }
    }));
  } catch (error) {
    console.error('[enrichBookings] Error enriching bookings:', error);
    return bookings;
  }
}

async function enrichBookingsWithResourceAndStaffInfo(bookings: Booking[]): Promise<Booking[]> {
  if (!bookings.length) return bookings;

  try {
    // Extract metadata from bookings (stored by mapWixBookingToBooking)
    const resourceIds = new Set<string>();
    const staffMemberIds = new Set<string>();
    
    bookings.forEach(booking => {
      const metadata = (booking as BookingWithMetadata)._resourceMetadata;
      if (metadata) {
        if (metadata.id) resourceIds.add(metadata.id);
        if (metadata.staffMemberId) staffMemberIds.add(metadata.staffMemberId);
      }
    });

    // Fetch resources and staff members in parallel
    const [resourcesResult, staffMembersResult] = await Promise.all([
      callAdminApi('queryResources', 'bookings', {}),
      callAdminApi('queryStaffMembers', 'bookings', {})
    ]);

    // Process resources
    const resourcesData = resourcesResult as { items?: Resource[] };
    const resources = resourcesData?.items || [];
    const resourceMap = new Map<string, Resource>();
    resources.forEach(resource => {
      if (resource._id) resourceMap.set(resource._id, resource);
    });

    // Process staff members
    const staffData = staffMembersResult as { items?: StaffMember[]; _items?: StaffMember[] };
    const staffMembers = staffData?.items || staffData?._items || [];
    const staffMap = new Map<string, StaffMember>();
    staffMembers.forEach(staff => {
      if (staff._id) staffMap.set(staff._id, staff);
    });

    // Enrich bookings with resource and staff names
    // slot.resource can contain EITHER a staff member OR a resource (team slot)
    // We need to check both APIs to determine which one it is
    const enrichedBookings = bookings.map(booking => {
      const metadata = (booking as BookingWithMetadata)._resourceMetadata;
      if (!metadata) return booking;

      let resourceName = booking.resource;
      let staffName = booking.staff;

      // Check if slot.resource._id is a staff member or a resource
      if (metadata.id) {
        // First check if it's a staff member
        const staffObj = staffMap.get(metadata.id);
        if (staffObj?.name) {
          // It's a staff member - assign to staff field
          staffName = staffObj.name;
        } else {
          // Not a staff member, check if it's a resource
          const resourceObj = resourceMap.get(metadata.id);
          if (resourceObj?.name) {
            // It's a resource (team slot) - assign to resource field
            resourceName = resourceObj.name;
          }
        }
      }

      // Also check slot.staffMemberId if present (alternative way Wix might store staff)
      if (metadata.staffMemberId && !staffName) {
        const staffObj = staffMap.get(metadata.staffMemberId);
        if (staffObj?.name) {
          staffName = staffObj.name;
        }
      }

      return {
        ...booking,
        resource: resourceName,  // Resource from Resources API (Nicole, Team Member 01, etc.)
        staff: staffName         // Staff from Staff Members API (Raquel Slater, etc.)
      };
    });

    return enrichedBookings;
  } catch (error) {
    console.error('[enrichBookings] Error enriching bookings with resource/staff info:', error);
    return bookings;
  }
}

export class BookingsApi implements BookingService {
  private serviceTypesApi: ServiceTypesApi;

  constructor() {
    this.serviceTypesApi = new ServiceTypesApi();
  }

  /**
   * Fetch bookings for a specific contact within a date range (no blocked slots).
   * @param contactId Contact ID to filter bookings
   * @param startDate Start of range (Date)
   * @param endDate End of range (Date)
   * @returns Promise<Booking[]>
   */
  async getBookingsByContactIdAndDateRange(contactId: string, startDate: Date, endDate: Date): Promise<Booking[]> {
    try {
      const result = await callAdminApi('queryBookings', 'bookings', {
        filter: {
          'contactDetails.contactId': { $eq: contactId },
          startDate: { $gte: startDate.toISOString() },
          endDate: { $lte: endDate.toISOString() },
          status: { $ne: 'CANCELED' } // Exclude canceled bookings (Wix uses CANCELED, not lowercase)
        },
        limit: 100
      });
      
      // Backend returns either items or extendedBookings
      const items = (result as { items?: Record<string, unknown>[], extendedBookings?: Record<string, unknown>[] })?.items || 
                   (result as { items?: Record<string, unknown>[], extendedBookings?: Record<string, unknown>[] })?.extendedBookings || [];
      
      const mappedBookings = items.map(mapWixBookingToBooking);
      return await enrichBookingsWithContactAvatars(mappedBookings);
    } catch {
      return [];
    }
  }
  async getAllBookings(params?: { offset?: number; limit?: number }): Promise<Booking[]> {
    try {
      const query: { offset?: number; limit?: number } = {};
      if (params) {
        if (typeof params.offset === 'number') query.offset = params.offset;
        if (typeof params.limit === 'number') query.limit = params.limit;
      }
      const result = await callAdminApi('queryBookings', 'bookings', query);
      
      // Backend returns either items or extendedBookings
      const items = (result as { items?: Record<string, unknown>[], extendedBookings?: Record<string, unknown>[] })?.items || 
                   (result as { items?: Record<string, unknown>[], extendedBookings?: Record<string, unknown>[] })?.extendedBookings || [];
      
      const mappedBookings = items.map(mapWixBookingToBooking);
      const enrichedWithContacts = await enrichBookingsWithContactAvatars(mappedBookings);
      const enrichedBookings = await enrichBookingsWithResourceAndStaffInfo(enrichedWithContacts);
      return enrichedBookings;
    } catch {
      return [];
    }
  }

  async getBookingById(id: string): Promise<Booking | null> {
    try {
      const result = await callAdminApi('getBooking', 'bookings', { 
        filter: { _id: id }
      });
      
      // Handle the case where the result comes from queryExtendedBookings (has extendedBookings array)
      let bookingData = result;
      const resultWithExtended = result as { extendedBookings?: Record<string, unknown>[] };
      if (Array.isArray(resultWithExtended?.extendedBookings)) {
        if (resultWithExtended.extendedBookings.length > 0) {
          bookingData = resultWithExtended.extendedBookings[0];
        } else {
          return null;
        }
      }
      
      const mappedBooking = mapWixBookingToBooking(bookingData as Record<string, unknown>);
      
      // Enrich with contact avatars and resource/staff info like other booking queries
      const enrichedWithContacts = await enrichBookingsWithContactAvatars([mappedBooking]);
      const enrichedBookings = await enrichBookingsWithResourceAndStaffInfo(enrichedWithContacts);
      return enrichedBookings[0] || null;
    } catch {
      return null;
    }
  }

  async createBooking(bookingData: BookingFormData): Promise<Booking> {
    const result = await this.createBookingWithAvailabilityCheck(bookingData);
    
    if (result.error) {
      throw new Error(result.error);
    }
    
    if (!result.booking) {
      throw new Error('Failed to create booking');
    }
    
    return result.booking;
  }

  async createBookingWithAvailabilityCheck(bookingData: BookingFormData, options?: { adminOverride?: boolean; userRole?: 'owner' | 'admin' | 'manager' | 'user' }): Promise<{
    booking?: Booking;
    warning?: string;
    error?: string;
    availabilityStatus: 'available' | 'unavailable' | 'admin_override';
  }> {
    try {
      // Get real service ID from Wix services based on service type
      let serviceId: string | null = null;
      
      try {
        const serviceTypes = await this.serviceTypesApi.getServiceTypes();
        const matchingService = serviceTypes.find(service => 
          service.type === bookingData.serviceType
        );
        
        if (matchingService && matchingService.serviceId) {
          serviceId = matchingService.serviceId;
        } else {
          // If no exact match, use the first available service
          if (serviceTypes.length > 0 && serviceTypes[0].serviceId) {
            serviceId = serviceTypes[0].serviceId;
          } else {
            throw new Error('No real services found in Wix');
          }
        }
      } catch (error) {
        throw new Error(`Failed to query services: ${error}`);
      }

      if (!serviceId) {
        throw new Error('Unable to determine real service ID for booking');
      }

      // Wix's own docs (Create Booking request schema) are explicit:
      // bookedEntity.slot.scheduleId must be the SERVICE's own schedule GUID,
      // not a staff/resource's individual event schedule — sending a
      // resource's scheduleId here produces undocumented, broken behavior
      // when skipAvailabilityValidation is true (confirmed live: Wix silently
      // stores the resource's ID into the booking's slot.serviceId instead of
      // the real service ID, which then 404s at confirm time since that
      // resource ID obviously isn't a valid service). Fetch it once up front
      // so admin-override code below has a correct baseline it must not
      // overwrite with a staff/resource schedule.
      let serviceScheduleId: string | null = null;
      let serviceImageUrl: string | null = null;
      try {
        const serviceDetails = await callAdminApi('getService', 'services', { id: serviceId }) as {
          schedule?: { _id?: string };
          media?: { mainMedia?: { image?: string } };
        };
        serviceScheduleId = serviceDetails?.schedule?._id || null;
        const rawImage = serviceDetails?.media?.mainMedia?.image;
        serviceImageUrl = rawImage ? convertWixImageUrl(rawImage) : null;
      } catch (error) {
        console.error('[createBookingWithAvailabilityCheck] Failed to fetch service schedule:', error);
      }

      // Get real availability data from Wix
      let availabilityStatus: 'available' | 'unavailable' | 'admin_override' = 'unavailable';
      let warningMessage: string | null = null;
      let scheduleId: string | null = serviceScheduleId; // Seeded from the service's own schedule; selected-slot logic below may refine it, but nothing should fall back to a resource's own event schedule
      let selectedSlotData: { slot?: Record<string, unknown>; bookable?: boolean } | null = null; // Store selected slot from availability query

      // Parse the booking times first - outside try-catch so they're accessible in catch block
      const startTime = bookingData.startTime instanceof Date ? bookingData.startTime : new Date(bookingData.startTime);
      const endTime = bookingData.endTime instanceof Date ? bookingData.endTime : new Date(bookingData.endTime);

      try {
        // Expand the search range to find available slots around the requested time
        const searchStartTime = new Date(startTime);
        searchStartTime.setHours(0, 0, 0, 0); // Start of day
        
        const searchEndTime = new Date(endTime);
        searchEndTime.setHours(23, 59, 59, 999); // End of day
        
        // Use proper query structure based on Wix SDK documentation
        const availabilityQuery = {
          filter: {
            serviceId: serviceId,
            startDate: searchStartTime.toISOString(),
            endDate: searchEndTime.toISOString()
          },
          // timezone and other options go outside the filter
          timezone: 'America/Los_Angeles',
          bookable: true
        };
        
        // Call admin API with proper structure
        const availabilityResult = await queryAvailabilityTimeSlots(availabilityQuery);

        // Look for available appointment slots
        const availabilityEntries = (availabilityResult as { 
          availabilityEntries?: Array<{
            slot?: {
              serviceId?: string;
              scheduleId?: string;
              startDate?: string;
              endDate?: string;
              resource?: {
                name?: string;
                _id?: string;
              };
              location?: {
                locationType?: string;
              };
            };
            bookable?: boolean;
          }>
        })?.availabilityEntries || [];

        
        // For appointment services, we use slot-based booking
        if (availabilityEntries.length > 0) {
          
          // Find ALL appointment slots (don't filter by bookable since they might be admin-bookable)
          const allSlots = availabilityEntries;

          // Determine if this caller is an admin/owner/manager/salesManager (they get more flexible booking options)
          const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
          
          // Try to find a slot that matches our requested time EXACTLY
          const exactMatchingSlot = allSlots.find(entry => {
            if (!entry.slot?.startDate || !entry.slot?.endDate) return false;
            
            const slotStart = new Date(entry.slot.startDate);
            const slotEnd = new Date(entry.slot.endDate);
            
            // Check for exact time match (within 1 minute tolerance)
            return Math.abs(slotStart.getTime() - startTime.getTime()) < 60000 && 
                   Math.abs(slotEnd.getTime() - endTime.getTime()) < 60000;
          });

          // Select the exact matching slot if found, otherwise use the old logic
          let selectedSlot = exactMatchingSlot;

          // If no exact match found, implement true admin override for minute-by-minute booking
          if (!selectedSlot) {
            if (isAdminUser) {
              // For admins, we can create a booking at ANY time using skipAvailabilityValidation
              // Create a virtual slot that matches the exact structure of real availability slots
              
              const adminVirtualSlot = {
                slot: {
                  serviceId: serviceId,
                  // Must be the service's own schedule (see serviceScheduleId
                  // fetch above) — allSlots[0] may be empty for an
                  // out-of-hours admin override, and serviceId itself is
                  // never a valid scheduleId.
                  scheduleId: allSlots[0]?.slot?.scheduleId || serviceScheduleId,
                  startDate: startTime.toISOString(),
                  endDate: endTime.toISOString(),
                  timezone: 'America/Los_Angeles',
                  resource: allSlots[0]?.slot?.resource || {
                    _id: serviceId,
                    name: 'Admin Override Resource'
                  },
                  location: {
                    locationType: 'CUSTOM',
                    // Always include the actual client address for custom locations
                    formattedAddress: bookingData.location || ''
                  }
                },
                bookable: false // This is an admin override, not a regular bookable slot
              };

              selectedSlot = adminVirtualSlot as typeof availabilityEntries[number];
              selectedSlotData = adminVirtualSlot;
              scheduleId = adminVirtualSlot.slot.scheduleId;
              availabilityStatus = 'admin_override';

              warningMessage = `Admin override: Booking created at exact requested time ${startTime.toISOString()} - ${endTime.toISOString()}`;
            } else {
              // Non-admin and no exact match -> cannot proceed
              throw new Error('Requested time does not match any exact available slot');
            }
          }
          
          if (selectedSlot) {
            // For appointments, we use slot information
            
            // Store the complete slot data from availability query (as per Wix documentation)
            selectedSlotData = selectedSlot;
            
            // Extract scheduleId from selected slot
            scheduleId = selectedSlot.slot?.scheduleId || null;
            
            if (selectedSlot.bookable) {
              availabilityStatus = 'available';
            } else {
              availabilityStatus = 'admin_override';
            }
          } else {
            // No slot was selected - check if admin can override
            const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
            
            if (isAdminUser) {
              // Create a virtual slot for admin override with exact requested times
            selectedSlot = {
              slot: {
                serviceId: serviceId,
                scheduleId: serviceScheduleId, // Service's own schedule (never serviceId itself)
                startDate: startTime.toISOString(),
                endDate: endTime.toISOString(),
                timezone: 'America/Los_Angeles',
                resource: {
                  _id: serviceId,
                  name: 'Admin Override Resource'
                },
                location: {
                  locationType: 'CUSTOM',
                  // Include the actual client address for custom locations
                  formattedAddress: bookingData.location || ''
                }
              },
              bookable: false
            } as typeof availabilityEntries[number];              selectedSlotData = selectedSlot;
              scheduleId = selectedSlot.slot?.scheduleId || null;
              availabilityStatus = 'admin_override';
              
              warningMessage = `Admin override: No slots available, creating booking at exact requested time ${startTime.toISOString()} - ${endTime.toISOString()}`;
            } else {
              throw new Error('No available appointment slots found from Wix API');
            }
          }
        } else {
          // availabilityEntries came back completely empty (no real Wix slots
          // at all for this service on this day) — this branch previously had
          // no else at all, so scheduleId/selectedSlotData silently stayed
          // unset and the booking fell through to the bare fallback slot
          // below (which has no scheduleId), producing a VALIDATION_FAILURE
          // ("scheduleId must be set") at createBooking. This is the exact
          // scenario admin-override bookings hit routinely (booking outside
          // normal availability), so it needs the same explicit handling as
          // the other admin-override branches above. isAdminUser is
          // re-declared here (not shared with the block above) since that
          // one is scoped inside the sibling `if (availabilityEntries.length
          // > 0)` branch.
          const isAdminUserForEmptyAvailability = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
          console.log('🔍 DIAGNOSTIC: availabilityEntries was empty, taking zero-availability branch. isAdminUser:', isAdminUserForEmptyAvailability, 'serviceScheduleId:', serviceScheduleId);
          if (isAdminUserForEmptyAvailability) {
            selectedSlotData = {
              slot: {
                serviceId: serviceId,
                scheduleId: serviceScheduleId,
                startDate: startTime.toISOString(),
                endDate: endTime.toISOString(),
                timezone: 'America/Los_Angeles',
                resource: {
                  _id: serviceId,
                  name: 'Admin Override Resource'
                },
                location: {
                  locationType: 'CUSTOM',
                  formattedAddress: bookingData.location || ''
                }
              },
              bookable: false
            };
            scheduleId = serviceScheduleId;
            availabilityStatus = 'admin_override';
            warningMessage = `Admin override: No availability entries returned for this service, creating booking at exact requested time ${startTime.toISOString()} - ${endTime.toISOString()}`;
          } else {
            throw new Error('No available appointment slots found from Wix API');
          }
        }
      } catch (availabilityCheckError) {
        console.log('🔍 DIAGNOSTIC: availability check try-block threw, falling into catch:', availabilityCheckError instanceof Error ? availabilityCheckError.message : availabilityCheckError);
        // If availability check fails, admins can still proceed with override
        const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
        
        if (isAdminUser) {
          // Create virtual slot data for admin override when availability check fails
          selectedSlotData = {
            slot: {
              serviceId: serviceId,
              scheduleId: serviceScheduleId, // Service's own schedule (never serviceId itself)
              startDate: startTime.toISOString(),
              endDate: endTime.toISOString(),
              timezone: 'America/Los_Angeles',
              resource: {
                _id: serviceId,
                name: 'Admin Override Resource'
              },
              location: {
                locationType: 'CUSTOM',
                // Include the actual client address for custom locations
                formattedAddress: bookingData.location || ''
              }
            },
            bookable: false
          };
          
          scheduleId = selectedSlotData.slot?.scheduleId as string || null;
          availabilityStatus = 'admin_override';
          
          warningMessage = `Admin override: Availability check failed, creating booking at exact requested time ${startTime.toISOString()} - ${endTime.toISOString()}`;
        } else {
          throw new Error('Unable to verify slot availability for appointment booking. Please try again.');
        }
      }

      // Appointment booking ready
      if (availabilityStatus === 'available' || availabilityStatus === 'admin_override') {
        // Ready to proceed with booking creation
      }

      // Store the selected slot for later use

      // Transform internal booking data to Wix API format - correct structure
      // The Wix SDK seems to have issues with Date object serialization, so we need to be careful about formats
      let startDateTime: Date;
      let endDateTime: Date;
      
      try {
        startDateTime = bookingData.startTime instanceof Date ? bookingData.startTime : new Date(bookingData.startTime);
        endDateTime = bookingData.endTime instanceof Date ? bookingData.endTime : new Date(bookingData.endTime);
        
        // Validate that we have valid Date objects
        if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
          throw new Error('Invalid date values provided');
        }
      } catch {
        throw new Error('Failed to parse start or end time');
      }

      // Build additional fields with conditional admin warning - exclude date fields that might confuse SDK
      const additionalFields = [
        { id: this.generateGUID(), key: 'notes', value: bookingData.notes || '' },
        { id: this.generateGUID(), key: 'location', value: bookingData.location || '' },
        { id: this.generateGUID(), key: 'price', value: bookingData.price?.toString() || '0' },
        { id: this.generateGUID(), key: 'duration', value: bookingData.duration?.toString() || '0' },
        { id: this.generateGUID(), key: 'resource', value: bookingData.resource || '' },
        { id: this.generateGUID(), key: 'staff', value: bookingData.staff || '' },
        { id: this.generateGUID(), key: 'availabilityStatus', value: availabilityStatus }
      ];

      // Add admin override times if this is an admin override
      if (availabilityStatus === 'admin_override') {
        additionalFields.push(
          { id: this.generateGUID(), key: 'adminOverrideStartTime', value: startDateTime.toISOString() },
          { id: this.generateGUID(), key: 'adminOverrideEndTime', value: endDateTime.toISOString() },
          { id: this.generateGUID(), key: 'originalSlotStartTime', value: String(selectedSlotData?.slot?.startDate || '') },
          { id: this.generateGUID(), key: 'originalSlotEndTime', value: String(selectedSlotData?.slot?.endDate || '') }
        );
      }

      // Add admin warning if present
      if (warningMessage) {
        additionalFields.push({ id: this.generateGUID(), key: 'adminWarning', value: warningMessage });
      }

      // Only use real contactId - no fallbacks allowed
      if (!bookingData.contactId || bookingData.contactId.trim() === '') {
        throw new Error('Valid contactId is required for booking creation - no generated IDs allowed');
      }

      // Determine if we need admin flow control settings first
      const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
      let flowControlSettings: Record<string, boolean> | undefined = isAdminUser
        ? {
            skipBusinessConfirmation: true
          }
        : undefined;

      // Check if admin is assigning specific staff/resources
      const hasCustomStaffOrResource = bookingData.staff || bookingData.resource;
      
      // ALWAYS use admin override when admin assigns specific staff/resources
      // This is critical - without skipAvailabilityValidation, Wix will override our assignments
      if (isAdminUser && hasCustomStaffOrResource) {
        // Use Wix admin override capabilities for admin users
        // This allows assigning specific staff members and resources that aren't available to clients
        flowControlSettings = {
          ...flowControlSettings,
          skipAvailabilityValidation: true,
          skipSelectedPaymentOptionValidation: true
        };
        console.log('🔧 ADMIN OVERRIDE: Enabled flow control settings for custom staff/resource assignment');
      } else if (isAdminUser && availabilityStatus === 'admin_override') {
        // Also use admin override when booking outside available slots
        flowControlSettings = {
          ...flowControlSettings,
          skipAvailabilityValidation: true,
          skipSelectedPaymentOptionValidation: true
        };
        console.log('🔧 ADMIN OVERRIDE: Enabled flow control settings for time override');
      }

      // Build the slot structure first so we can reference it in the top-level resource field
      const builtSlot = await (async () => {
        // Build the slot structure
        const baseSlot: Record<string, unknown> = selectedSlotData?.slot || {
          serviceId: serviceId,
          scheduleId: serviceScheduleId,
          startDate: startDateTime.toISOString(),
          endDate: endDateTime.toISOString(),
          timezone: 'America/Los_Angeles',
          resource: {
            _id: serviceId
          },
          location: {
            locationType: 'CUSTOM',
            formattedAddress: bookingData.location || ''
          }
        };

        // Wix's CreateBooking API only reads location from bookedEntity.slot.location
        // (there is no top-level location field). A real matched slot from
        // queryAvailability doesn't carry the address chosen in this booking, so it
        // must always be stamped here regardless of whether baseSlot came from a
        // real slot or the virtual/admin-override default above.
        baseSlot.location = {
          locationType: 'CUSTOM',
          formattedAddress: bookingData.location || ''
        };

        // If admin is assigning specific staff/resources, look them up and add to slot
        if (isAdminUser && flowControlSettings && (bookingData.staff || bookingData.resource)) {
          try {
            // Query all resources and staff to find the IDs
            console.log('🔍 Querying resources and staff...');
            const [resourcesResult, staffResult] = await Promise.all([
              callAdminApi('queryResources', 'bookings', {}),
              callAdminApi('queryStaffMembers', 'bookings', {})
            ]);
            
            console.log('🔍 RAW resources result:', JSON.stringify(resourcesResult, null, 2));
            console.log('🔍 RAW staff result:', JSON.stringify(staffResult, null, 2));
            
            // Wix API returns _items (with underscore), not items. Each resource's
            // schedule ID is nested under eventsSchedule.scheduleId, not a flat
            // top-level scheduleId field (confirmed against the actual API response).
            const resourcesData = resourcesResult as { _items?: Array<{ _id?: string; name?: string; type?: string; eventsSchedule?: { scheduleId?: string } }> };
            const resources = resourcesData?._items || [];
            
            const staffData = staffResult as { _items?: Array<{ _id?: string; name?: string; scheduleId?: string; resourceId?: string }> };
            const staff = staffData?._items || [];
            
            console.log('🔍 ALL STAFF MEMBERS:', staff.length, 'items');
            console.log('🔍 ALL RESOURCES:', resources.length, 'items');
            console.log('🔍 Looking for staff:', bookingData.staff);
            console.log('🔍 Looking for resource:', bookingData.resource);
            
            // IMPORTANT: All staff members exist in BOTH staff and resources lists
            // - Staff list: has name, email, phone, resourceId (points to resource)
            // - Resources list: has _id (the actual resource), name, scheduleId
            // For Wix Bookings:
            // - slot.resource = PRIMARY staff member's resource (use staff's resourceId)
            // - slot.additionalResources = TEAM/additional resources (use resources' _id directly)
            
            // Wix's own booking UI always populates resourceSelections alongside
            // resource/additionalResources — it's what actually drives resolving
            // the resource into a real calendar session at confirm time. Omitting
            // it (as this admin-override path previously did) is what caused
            // confirmOrDeclineBooking to fail with "Entity not found" on the
            // primary resource for team/group staff entries that have no
            // associatedWixIdentity: Wix's own dashboard booking for the same
            // resource works fine, so the gap was in our request, not the resource.
            const resourceSelections: Array<{ resourceTypeId: string; selectionMethod: string }> = [];

            // Find the assigned staff member by name (primary worker)
            if (bookingData.staff) {
              const assignedStaff = staff.find(s => s.name === bookingData.staff);
              if (assignedStaff && assignedStaff.resourceId) {
                console.log('✅ Found staff member:', assignedStaff.name, 'resourceId:', assignedStaff.resourceId);
                // Use the resourceId from staff to set the primary resource
                baseSlot.resource = {
                  _id: assignedStaff.resourceId,
                  name: assignedStaff.name
                };
                // scheduleId must stay the SERVICE's own schedule (seeded
                // above from serviceScheduleId) — NOT this staff member's
                // individual event schedule. Sending the resource's schedule
                // here is what caused Wix to silently store the resource's ID
                // into the booking's slot.serviceId (confirmed via a raw
                // getBooking read-back on a live booking), which then 404s at
                // confirm time. Still need the resource entry for its `type`
                // (used by resourceSelections below).
                const staffResourceEntry = resources.find(r => r._id === assignedStaff.resourceId);
                if (staffResourceEntry?.type) {
                  resourceSelections.push({
                    resourceTypeId: staffResourceEntry.type,
                    selectionMethod: 'SPECIFIC_RESOURCE'
                  });
                }
              } else {
                console.warn('❌ Staff member not found:', bookingData.staff);
                console.warn('Available staff:', staff.map(s => s.name).join(', '));
              }
            }

            // Find the assigned team/additional resource by name in RESOURCES list
            if (bookingData.resource) {
              // Get allowed resource types from bookingData if available
              const allowedTypes = bookingData.resourceTypeIds;
              console.log('🔍 Looking for resource:', bookingData.resource);
              console.log('🔍 Allowed resource types:', allowedTypes);

              // Look up in resources list (not staff) - try exact match first, then partial match
              // If we have allowed types, filter by those types first to avoid matching wrong resource
              let candidateResources = allowedTypes && allowedTypes.length > 0
                ? resources.filter(r => r.type && allowedTypes.includes(r.type))
                : resources;

              console.log(`🔍 Searching in ${candidateResources.length} resources (filtered by type)`);

              let assignedResource = candidateResources.find(r => r.name === bookingData.resource);
              if (!assignedResource) {
                // Try partial match - useful when UI shows short name but API has full name
                const searchName = bookingData.resource.toLowerCase();
                assignedResource = candidateResources.find(r =>
                  r.name?.toLowerCase().includes(searchName) ||
                  searchName.includes(r.name?.toLowerCase() || '')
                );
              }

              if (assignedResource && assignedResource._id) {
                console.log('✅ Found team resource:', assignedResource.name, 'resourceId:', assignedResource._id, 'type:', assignedResource.type);
                // Add the team resource to additionalResources using the resource's _id directly
                baseSlot.additionalResources = [{
                  _id: assignedResource._id,
                  name: assignedResource.name
                }];
                if (assignedResource.type) {
                  resourceSelections.push({
                    resourceTypeId: assignedResource.type,
                    selectionMethod: 'SPECIFIC_RESOURCE'
                  });
                }
              } else {
                console.warn('❌ Team resource not found:', bookingData.resource);
                console.warn('Available resources:', candidateResources.map(r => `${r.name} (${r.type})`).join(', '));
              }
            }

            if (resourceSelections.length > 0) {
              baseSlot.resourceSelections = resourceSelections;
            }

            console.log('🔧 ADMIN OVERRIDE: Built custom slot with staff and resource:', JSON.stringify(baseSlot, null, 2));
          } catch (error) {
            console.error('❌ Failed to lookup staff/resources:', error);
          }
        }
        
        return baseSlot;
      })();

      const wixBookingData: Record<string, unknown> = {
        totalParticipants: 1,
        contactDetails: {
          firstName: bookingData.contact.name.split(' ')[0] || '',
          lastName: bookingData.contact.name.split(' ').slice(1).join(' ') || '',
          email: bookingData.contact.email,
          phone: bookingData.contact.phone,
          contactId: bookingData.contactId
        },
        // Always include bookedEntity as it's required by Wix
        // Use the complete slot structure from availability query as recommended by Wix docs
        bookedEntity: {
          slot: builtSlot
        },
        // Required top-level fields for Wix API — the SDK's own serializer calls
        // .toISOString() on these when building the REST request (see
        // transformSDKTimestampToRESTTimestamp in @wix/sdk-runtime), so it needs
        // real Date instances here, not pre-stringified ISO strings.
        startDate: startDateTime,
        endDate: endDateTime,
        location: {
          locationType: 'CUSTOM',
          // Include the actual client address for custom locations
          formattedAddress: bookingData.location || ''
        },
        // Add scheduleId if available from selected slot
        ...(scheduleId && { scheduleId }),
        additionalFields
      };
      
      // Wix requires resource.id at the top level whenever sessionId isn't set
      // (alongside startDate/endDate/location.locationType/scheduleId — see the
      // VALIDATION_FAILURE this used to throw when this field was conditionally
      // omitted for admin-assigned staff/resources). Sourced from the same
      // resolved resource the slot already carries, so the two always agree.
      wixBookingData.resource = {
        id: (builtSlot.resource as { _id?: string } | undefined)?._id || serviceId
      };

      // Use the standard API call for booking creation with optional admin override
      // For Wix API, flowControlSettings must be passed as a separate options parameter, not merged into booking data
      const createBookingParams = flowControlSettings 
        ? {
            bookingData: wixBookingData,
            options: {
              flowControlSettings
            }
          }
        : { bookingData: wixBookingData, options: {} };
        
      console.log('🔍 DIAGNOSTIC: final createBookingParams about to be sent:', JSON.stringify(createBookingParams, null, 2));
      const result = await callAdminApi('createBooking', 'bookings', createBookingParams);
      const booking = result as unknown as Booking;
      console.log('🔍 DIAGNOSTIC: raw createBooking result:', JSON.stringify(result, null, 2));

      // A freshly created booking always comes back with status CREATED and
      // isn't visible in the booking calendar (see Wix's own "Flow:
      // Single-Service Booking" docs) — it only becomes CONFIRMED, and shows
      // as a real appointment, once confirmed. Normally the eCommerce
      // checkout flow does this automatically after payment; admin-created
      // bookings skip that entirely. The 'createBooking'/
      // 'createBookingWithAvailabilityCheck' handlers now do that confirm
      // step themselves, server-side, immediately after create — calling it
      // again from here would just be a second, redundant confirm attempt
      // against an already-confirmed booking (and a needless extra
      // round-trip, which was the actual cause of the stale-revision
      // failures this used to hit). `result` below is already the confirmed
      // booking.
      const resultObj = result as unknown as {
        booking?: { booking?: { _id?: string }; _id?: string };
      };
      const createdBookingId = resultObj?.booking?.booking?._id || resultObj?.booking?._id;
      if (createdBookingId) {
        // Native Wix admin-created bookings get a plain, blank internal
        // order marked paid, mirroring what eCommerce checkout does
        // automatically for customer bookings. Always $0 — bookingData.price
        // is the Zoho service-order draft total, a separate system's real
        // invoice amount that must never be sent to Wix (handled entirely
        // by the backend handler; nothing price-related passed here).
        // Non-fatal on failure — the booking is already created and
        // confirmed regardless.
        try {
          await callAdminApi('createBookingOrder', 'bookings', {
            bookingId: createdBookingId,
            serviceName: bookingData.serviceType,
            contactId: bookingData.contactId,
            serviceImageUrl,
            contactName: bookingData.contact.name,
            contactEmail: bookingData.contact.email,
            contactPhone: bookingData.contact.phone,
          });
        } catch (orderError) {
          console.error('[createBookingWithAvailabilityCheck] Failed to create order for booking:', orderError);
        }
      } else {
        console.error('[createBookingWithAvailabilityCheck] Could not find booking ID on create result:', result);
      }

      // Notify all tabs that a booking was created
      DataStorageService.notifyBookingChanged();

      return {
        booking,
        warning: warningMessage || undefined,
        availabilityStatus
      };
    } catch (error) {
      return {
        error: `Failed to create booking: ${error instanceof Error ? error.message : 'Unknown error'}`,
        availabilityStatus: 'unavailable'
      };
    }
  }

  private mapStatusToWixEnum(status: string): string {
    // Booking.status is typed BookingStatusWithLiterals — a string enum
    // ('CREATED' | 'CONFIRMED' | 'CANCELED' | 'PENDING' | 'DECLINED' |
    // 'WAITING_LIST'), never a number. This used to return 0/1/2/3, which
    // doesn't match that type at all.
    const statusMap: { [key: string]: string } = {
      'pending': 'PENDING',
      'confirmed': 'CONFIRMED',
      'completed': 'CONFIRMED', // Use CONFIRMED for completed (closest match)
      'cancelled': 'CANCELED'
    };

    return statusMap[status] || 'CREATED'; // Default to CREATED if status not found
  }

  private generateGUID(): string {
    // Generate UUID for additional fields only
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  async updateBooking(booking: Booking): Promise<Booking> {
    try {
      // Get the booking to retrieve its revision (same method as cancelBooking)
      const bookingResult = await callAdminApi('getBooking', 'bookings', { 
        filter: { _id: booking.id }
      });
      console.log('🔍 Booking result for revision:', bookingResult);
      
      // Extract revision, scheduleId, and resourceId from the booking data (same structure as cancelBooking uses)
      let revision: number | undefined;
      let scheduleId: string | undefined;
      let resourceId: string | undefined;
      let existingStartDate: string | undefined;
      let existingEndDate: string | undefined;
      const resultWithExtended = bookingResult as { extendedBookings?: Array<{ booking?: { revision?: string | number; bookedEntity?: { scheduleId?: string; slot?: { scheduleId?: string; startDate?: string; endDate?: string; resource?: { _id?: string } } }; scheduleId?: string } }> };
      if (Array.isArray(resultWithExtended?.extendedBookings) && resultWithExtended.extendedBookings.length > 0) {
        const bookingData = resultWithExtended.extendedBookings[0]?.booking;
        const rev = bookingData?.revision;
        revision = typeof rev === 'string' ? parseInt(rev, 10) : rev;

        // Try to find scheduleId in various possible locations
        scheduleId = bookingData?.bookedEntity?.scheduleId ||
                    bookingData?.bookedEntity?.slot?.scheduleId ||
                    bookingData?.scheduleId;

        // Extract resourceId and existing times from the slot
        resourceId = bookingData?.bookedEntity?.slot?.resource?._id;
        existingStartDate = bookingData?.bookedEntity?.slot?.startDate;
        existingEndDate = bookingData?.bookedEntity?.slot?.endDate;
        
        console.log('📋 FULL BOOKING DATA STRUCTURE:', JSON.stringify(bookingData, null, 2));
        console.log('📋 bookedEntity:', JSON.stringify(bookingData?.bookedEntity, null, 2));
        console.log('📋 slot:', JSON.stringify(bookingData?.bookedEntity?.slot, null, 2));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        console.log('📋 participantNotification:', JSON.stringify((bookingData as any)?.participantNotification, null, 2));
      }
      
      console.log('📌 Extracted revision:', revision);
      console.log('📌 Extracted scheduleId:', scheduleId);
      console.log('📌 Extracted resourceId:', resourceId);
      
      if (!revision) {
        console.error('❌ No revision found in booking result:', bookingResult);
        throw new Error('Unable to get booking revision for update');
      }
      
      // Get real service ID from Wix services based on service type
      let serviceId: string | null = null;
      
      try {
        const serviceTypes = await this.serviceTypesApi.getServiceTypes();
        const matchingService = serviceTypes.find(service => 
          service.type === booking.serviceType
        );
        
        if (matchingService && matchingService.serviceId) {
          serviceId = matchingService.serviceId;
        } else {
          // If no exact match, use the first available service
          if (serviceTypes.length > 0 && serviceTypes[0].serviceId) {
            serviceId = serviceTypes[0].serviceId;
          } else {
            throw new Error('No real services found in Wix');
          }
        }
      } catch (error) {
        throw new Error(`Failed to query services: ${error}`);
      }

      if (!serviceId) {
        throw new Error('Unable to determine real service ID for booking update');
      }

      // Get real availability data for the booking time (appointment-only)
      try {
        const startTime = booking.startTime instanceof Date ? booking.startTime : new Date(booking.startTime);
        const endTime = booking.endTime instanceof Date ? booking.endTime : new Date(booking.endTime);
        
        // Expand the search range to find available slots around the requested time
        const searchStartTime = new Date(startTime);
        searchStartTime.setHours(0, 0, 0, 0); // Start of day
        
        const searchEndTime = new Date(endTime);
        searchEndTime.setHours(23, 59, 59, 999); // End of day
        
        // Query Wix for available appointment slots
        const availabilityResult = await queryAvailabilityTimeSlots({
          filter: {
            serviceId: serviceId,
            startDate: searchStartTime.toISOString(),
            endDate: searchEndTime.toISOString()
          },
          timezone: 'America/Los_Angeles',
          bookable: true
        });

        // Look for available appointment slots
        const availabilityEntries = (availabilityResult as { 
          availabilityEntries?: Array<{
            slot?: {
              serviceId?: string;
              startDate?: string;
              endDate?: string;
            };
            bookable?: boolean;
          }>
        })?.availabilityEntries || [];

        // Find bookable slots
        const bookableSlots = availabilityEntries.filter(entry => entry.bookable === true);
        
        // Find a slot that matches our requested time
        const matchingSlot = bookableSlots.find(entry => {
          if (!entry.slot?.startDate || !entry.slot?.endDate) return false;
          
          const slotStart = new Date(entry.slot.startDate);
          const slotEnd = new Date(entry.slot.endDate);
          
          // Check if this slot covers our requested time exactly or contains it
          return slotStart <= startTime && slotEnd >= endTime;
        });

        // Use matching slot or any available slot
        const selectedSlot = matchingSlot || (bookableSlots.length > 0 ? bookableSlots[0] : null);

        if (!selectedSlot) {
          // No available appointment slots found for update - proceeding with admin override
        }
      } catch {
        // Proceeding with appointment update without availability check
      }

      // Transform booking data to Wix API format
      const startDateTime = booking.startTime instanceof Date ? booking.startTime : new Date(booking.startTime);
      const endDateTime = booking.endTime instanceof Date ? booking.endTime : new Date(booking.endTime);
      
      // Build the slot object for rescheduleBooking.
      // Valid Slot fields per SDK type: serviceId, scheduleId, startDate, endDate,
      // timezone, resource, location, eventId.
      // sessionId and additionalResources are NOT valid Slot fields and are silently dropped.
      const slot: Record<string, unknown> = {
        scheduleId,
        serviceId: serviceId,
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        timezone: 'America/Los_Angeles',
        location: {
          locationType: 'CUSTOM',
          formattedAddress: booking.location || ''
        }
      };

      // Handle staff and resource assignments (same logic as create booking)
      // IMPORTANT: All staff members exist in BOTH staff and resources lists
      // - Staff list: has name, email, phone, resourceId (points to resource)
      // - Resources list: has _id (the actual resource), name, scheduleId
      // For Wix Bookings:
      // - slot.resource = PRIMARY staff member's resource (use staff's resourceId)
      // - slot.additionalResources = TEAM/additional resources (use resources' _id directly)
      
      try {
        // Query all resources and staff to find the IDs (same as create booking)
        console.log('🔍 UPDATE: Querying resources and staff...');
        const [resourcesResult, staffResult] = await Promise.all([
          callAdminApi('queryResources', 'bookings', {}),
          callAdminApi('queryStaffMembers', 'bookings', {})
        ]);
        
        // Wix API returns _items (with underscore), not items
        const resourcesData = resourcesResult as { _items?: Array<{ _id?: string; name?: string; scheduleId?: string; type?: string }> };
        const resources = resourcesData?._items || [];
        
        const staffData = staffResult as { _items?: Array<{ _id?: string; name?: string; scheduleId?: string; resourceId?: string }> };
        const staff = staffData?._items || [];
        
        console.log('🔍 UPDATE: ALL STAFF MEMBERS:', staff.length, 'items');
        console.log('🔍 UPDATE: ALL RESOURCES:', resources.length, 'items');
        console.log('� UPDATE: Looking for staff:', booking.staff);
        console.log('� UPDATE: Looking for resource:', booking.resource);
        
        // Find the assigned staff member by name (primary worker)
        // NOTE: Staff can work on any service regardless of their resource type
        if (booking.staff) {
          const assignedStaff = staff.find(s => s.name === booking.staff);
          if (assignedStaff && assignedStaff.resourceId) {
            console.log('✅ UPDATE: Found staff member:', assignedStaff.name, 'resourceId:', assignedStaff.resourceId);
            // Use the resourceId from staff to set the primary resource
            // Staff resources are not filtered by service type - staff can work any service
            slot.resource = {
              _id: assignedStaff.resourceId,
              name: assignedStaff.name
            };
          } else {
            console.warn('❌ UPDATE: Staff member not found:', booking.staff);
            console.warn('Available staff:', staff.map(s => s.name).join(', '));
          }
        } else if (resourceId) {
          // If no staff specified, keep the existing primary resource
          console.log('📌 UPDATE: No staff specified, keeping existing resource:', resourceId);
          slot.resource = { _id: resourceId };
        }
        
        // Find the assigned team/additional resource by name in RESOURCES list
        if (booking.resource) {
          // Get allowed resource types from booking if available
          const allowedTypes = booking.resourceTypeIds;
          console.log('🔍 UPDATE: Looking for resource:', booking.resource);
          console.log('🔍 UPDATE: Allowed resource types:', allowedTypes);
          
          // Look up in resources list (not staff) - try exact match first, then partial match
          // If we have allowed types, filter by those types first to avoid matching wrong resource
          let candidateResources = allowedTypes && allowedTypes.length > 0
            ? resources.filter(r => r.type && allowedTypes.includes(r.type))
            : resources;
          
          console.log(`🔍 UPDATE: Searching in ${candidateResources.length} resources (filtered by type)`);
          
          let assignedResource = candidateResources.find(r => r.name === booking.resource);
          if (!assignedResource) {
            // Try partial match - useful when UI shows short name but API has full name
            const searchName = booking.resource.toLowerCase();
            assignedResource = candidateResources.find(r => 
              r.name?.toLowerCase().includes(searchName) || 
              searchName.includes(r.name?.toLowerCase() || '')
            );
          }
          
          if (assignedResource && assignedResource._id) {
            console.log('✅ UPDATE: Found team resource:', assignedResource.name, 'resourceId:', assignedResource._id, 'type:', assignedResource.type);
          } else {
            console.warn('❌ UPDATE: Team resource not found:', booking.resource);
            console.warn('Available resources:', candidateResources.map(r => `${r.name} (${r.type})`).join(', '));
          }
        }
        
        console.log('� UPDATE: Built slot with staff and resource:', JSON.stringify(slot, null, 2));
      } catch (error) {
        console.error('❌ UPDATE: Failed to lookup staff/resources:', error);
      }

      // Skip rescheduleBooking if time hasn't changed — Wix rejects same-slot reschedules
      // (e.g. when only address or staff metadata changed)
      const newStart = booking.startTime instanceof Date ? booking.startTime : new Date(booking.startTime);
      const newEnd = booking.endTime instanceof Date ? booking.endTime : new Date(booking.endTime);
      const existingStart = existingStartDate ? new Date(existingStartDate) : null;
      const existingEnd = existingEndDate ? new Date(existingEndDate) : null;
      const timeUnchanged = existingStart && existingEnd &&
        Math.abs(existingStart.getTime() - newStart.getTime()) < 60000 &&
        Math.abs(existingEnd.getTime() - newEnd.getTime()) < 60000;

      if (timeUnchanged && !booking.location) {
        console.log('📌 Time and location both unchanged — skipping rescheduleBooking');
        DataStorageService.notifyBookingChanged();
        return booking;
      }

      // Reschedule the booking (updates slot/time/resource)
      console.log('📌 CALLING rescheduleBooking with slot:', JSON.stringify(slot, null, 2));
      console.log('📌 CALLING rescheduleBooking with revision:', revision);

      const rescheduleResult = await callAdminApi('updateBooking', 'bookings', { 
        id: booking.id,
        slot: slot,
        options: {
          revision: revision,
          flowControlSettings: {
            ignoreReschedulePolicy: true,
            skipAvailabilityValidation: true,
            skipBusinessConfirmation: true
          }
        }
      });

      console.log('📌 RESCHEDULE RESULT:', JSON.stringify(rescheduleResult, null, 2));
      
      // Note: Form fields (notes, price, duration) are stored as additionalFields in the form submission
      // and may not be updatable after creation. Resource is updated via slot.resource above.
      // Staff member assignment would need a separate API if Wix provides one.
      
      // Notify all tabs that a booking was updated
      DataStorageService.notifyBookingChanged();
      
      return rescheduleResult as unknown as Booking;
    } catch (error) {
      console.error('Error updating booking:', error);
      throw new Error(`Failed to update booking: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ── Booking notes ────────────────────────────────────────────────────────
  // A Wix booking's notes (the ones staff edit in the Wix dashboard) live on the booking's
  // calendar event — `event.notes`, "Additional notes about the event", business-only — and are
  // changed with the calendar's updateEvent, the same call blocked-time notes use. They are NOT the
  // booking's form fields (written once at creation), which updateBooking cannot change.

  /** The booking's calendar event (id, revision, notes), or null if it cannot be found. */
  private async getBookingCalendarEvent(bookingId: string): Promise<{
    eventId: string;
    revision?: string | number;
    notes: string;
    startLocalDate?: string;
    endLocalDate?: string;
  } | null> {
    const bookingResult = await callAdminApi('getBooking', 'bookings', { filter: { _id: bookingId } }) as {
      extendedBookings?: Array<{ booking?: { bookedEntity?: { slot?: { eventId?: string; sessionId?: string } } } }>;
    };
    const slot = bookingResult?.extendedBookings?.[0]?.booking?.bookedEntity?.slot;
    const eventId = slot?.eventId || slot?.sessionId;
    if (!eventId) return null;

    const eventResult = await callAdminApi('getEvent', 'bookings', { id: eventId }) as Record<string, unknown>;
    const event = ((eventResult?.['event'] as Record<string, unknown> | undefined) ?? eventResult) as Record<string, unknown>;
    const localDateOf = (value: unknown): string | undefined => {
      const v = value as { localDate?: unknown } | undefined;
      return typeof v?.localDate === 'string' ? v.localDate : undefined;
    };
    return {
      eventId,
      revision: event?.['revision'] as string | number | undefined,
      notes: typeof event?.['notes'] === 'string' ? (event['notes'] as string) : '',
      startLocalDate: localDateOf(event?.['start']),
      endLocalDate: localDateOf(event?.['end']),
    };
  }

  /** The booking's Wix notes (empty string if none). */
  async getBookingNotes(bookingId: string): Promise<string> {
    try {
      return (await this.getBookingCalendarEvent(bookingId))?.notes ?? '';
    } catch (error) {
      console.error('Error reading booking notes:', error);
      return '';
    }
  }

  /** Saves the booking's notes to Wix. Throws if the booking's event cannot be found or updated. */
  async updateBookingNotes(bookingId: string, notes: string): Promise<void> {
    const calendarEvent = await this.getBookingCalendarEvent(bookingId);
    if (!calendarEvent) throw new Error('Could not find the calendar event for this booking');
    if (!calendarEvent.startLocalDate || !calendarEvent.endLocalDate) {
      throw new Error('The booking\'s calendar event has no start / end time to resend');
    }
    await callAdminApi('updateEvent', 'bookings', {
      id: calendarEvent.eventId,
      event: {
        // Wix rejects an empty string (a note must be at least 1 character); `null` is how a
        // note is removed.
        notes: notes.trim() ? notes : null,
        // Wix's updateEvent insists on the event's start and end being sent with any update.
        // They are resent exactly as read, so the event's time does not change.
        start: { localDate: calendarEvent.startLocalDate },
        end: { localDate: calendarEvent.endLocalDate },
        ...(calendarEvent.revision !== undefined ? { revision: calendarEvent.revision } : {}),
      },
    });
    DataStorageService.notifyBookingChanged();
  }

  async deleteBooking(id: string): Promise<boolean> {
    try {
      await callAdminApi('deleteBooking', 'bookings', { id });
      
      // Notify all tabs that a booking was deleted
      DataStorageService.notifyBookingChanged();
      
      return true;
    } catch {
      return false;
    }
  }

  async getBookingsByDateRange(startDate: Date, endDate: Date): Promise<Booking[]> {
    const allItems: Record<string, unknown>[] = [];
    let cursor: string | undefined;
    let hasMore = true;

    try {
      while (hasMore) {
        const queryParams: Record<string, unknown> = cursor
          ? { cursorPaging: { cursor } }
          : {
              filter: {
                startDate: { $lte: endDate.toISOString() },
                endDate:   { $gte: startDate.toISOString() }
              },
              limit: 100
            };

        const result = await callAdminApi('queryBookings', 'bookings', queryParams) as {
          items?: Record<string, unknown>[];
          extendedBookings?: Record<string, unknown>[];
          pagingMetadata?: { hasNext: boolean; cursors?: { next?: string } };
        };

        const page = result?.items || result?.extendedBookings || [];
        allItems.push(...page);

        hasMore = !!result?.pagingMetadata?.hasNext;
        cursor = result?.pagingMetadata?.cursors?.next;
        if (allItems.length > 2000) break;
      }

      const mappedBookings = allItems.map(mapWixBookingToBooking);
      return await enrichBookingsWithContactAvatars(mappedBookings);
    } catch {
      return [];
    }
  }

  // Blocked-time calendar events only — skips queryBlockedSlotsInternal's
  // availability-derived policy computation (processBlockedSlots, over
  // thousands of availability entries) and the per-schedule getSchedule
  // enrichment (processSchedulesForBlockedTime), neither of which apply to
  // real blocked-time entries. serviceIds: [] already puts the backend
  // handler in events-only mode — used elsewhere in this file for exactly
  // this purpose, just always paired with the heavy call too. Callers that
  // only need to know "is there blocked time in this window" (e.g. Firestore
  // reconciliation) should use this instead of getBookingsAndBlockedSlotsByDateRange.
  async getBlockedTimeEventsOnly(startDate: Date, endDate: Date): Promise<Booking[]> {
    try {
      const [resourcesQueryResult, staffQueryResult, schedulesQueryResult, blockedSlotsResult] = await Promise.all([
        callAdminApi('queryResources', 'bookings', {}),
        callAdminApi('queryStaffMembers', 'bookings', {}),
        callAdminApi('querySchedules', 'bookings', {}),
        callAdminApi('queryBlockedSlots', 'bookings', {
          serviceIds: [],
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }),
      ]);

      const resourcesData = resourcesQueryResult as { items?: unknown[]; _items?: unknown[] };
      const resources = (resourcesData?.items || resourcesData?._items || []) as Resource[];

      const staffData = staffQueryResult as { items?: unknown[]; _items?: unknown[] };
      const staffMembers = (Array.isArray(staffQueryResult) ? staffQueryResult : (staffData?.items || staffData?._items || [])) as StaffMember[];

      const schedulesData = schedulesQueryResult as { items?: unknown[]; _items?: unknown[] };
      const schedules = (schedulesData?.items || schedulesData?._items || []) as Schedule[];

      const resourceMap = new Map<string, Resource>();
      resources.forEach(resource => { if (resource._id) resourceMap.set(resource._id, resource); });

      const staffMap = new Map<string, StaffMember>();
      staffMembers.forEach(staff => {
        if (staff._id) staffMap.set(staff._id, staff);
        if (staff.resourceId) staffMap.set(staff.resourceId, staff);
      });

      const scheduleMap = new Map<string, Schedule>();
      schedules.forEach(schedule => { if (schedule._id) scheduleMap.set(schedule._id, schedule); });

      const calendarEventsRaw = (blockedSlotsResult as { calendarEvents?: unknown })?.calendarEvents;
      const calendarEvents = Array.isArray(calendarEventsRaw)
        ? calendarEventsRaw
        : ((calendarEventsRaw as { items?: unknown[]; _items?: unknown[] } | undefined)?.items
          || (calendarEventsRaw as { items?: unknown[]; _items?: unknown[] } | undefined)?._items
          || []);

      const processed = this.processCalendarEvents(calendarEvents, resourceMap, staffMap, scheduleMap);
      return this.convertAgendaItemsToBookings(processed, new Map());
    } catch {
      return [];
    }
  }

  async getBookingsForDay(date: Date): Promise<Booking[]> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    return this.getBookingsByDateRange(startOfDay, endOfDay);
  }

  async getUpcomingBookings(limit?: number): Promise<Booking[]> {
    try {
      const result = await callAdminApi('queryBookings', 'bookings', {
        filter: {
          startDate: new Date().toISOString()
        },
        limit: limit || 50
      });
      
      // Backend returns either items or extendedBookings
      const items = (result as { items?: Record<string, unknown>[], extendedBookings?: Record<string, unknown>[] })?.items || 
                   (result as { items?: Record<string, unknown>[], extendedBookings?: Record<string, unknown>[] })?.extendedBookings || [];
      
      const mappedBookings = items.map(mapWixBookingToBooking);
      return await enrichBookingsWithContactAvatars(mappedBookings);
    } catch {
      return [];
    }
  }

  async updateBookingStatus(id: string, isCompleted: boolean): Promise<void> {
    try {
      await callAdminApi('updateBookingStatus', 'bookings', { id, isCompleted });
    } catch (error) {
      throw error;
    }
  }

  async setBookingAttended(id: string): Promise<void> {
    await callAdminApi('setBookingAttendance', 'bookings', { id, status: 'ATTENDED' });
  }

  async getBookingsAttendance(ids: string[]): Promise<Record<string, string>> {
    if (!ids.length) return {};
    const result = await callAdminApi('getBookingsAttendance', 'bookings', { ids }) as { statuses?: Record<string, string> };
    return result?.statuses ?? {};
  }

  async cancelBooking(id: string, waiveFee: boolean = true): Promise<void> {
    try {
      // First, get the booking to retrieve its revision number (required by Wix API)
      const bookingResult = await callAdminApi('getBooking', 'bookings', { 
        filter: { _id: id }
      });
      
      // Extract revision from the booking data
      let revision: number | undefined;
      const resultWithExtended = bookingResult as { extendedBookings?: Array<{ booking?: { revision?: string | number } }> };
      if (Array.isArray(resultWithExtended?.extendedBookings) && resultWithExtended.extendedBookings.length > 0) {
        const firstExtended = resultWithExtended.extendedBookings[0];
        console.log('cancelBooking: first extendedBooking:', JSON.stringify(firstExtended, null, 2));
        
        // Try direct revision
        let rev: string | number | undefined = firstExtended?.booking?.revision;
        if (!rev) {
          // Try alternate path
          rev = (firstExtended as Record<string, unknown>)?.revision as string | number | undefined;
        }
        if (!rev) {
          // Try on the object itself
          const booking = (firstExtended as Record<string, unknown>)?.booking as Record<string, unknown> | undefined;
          rev = booking?._revision as string | number | undefined;
        }
        
        revision = typeof rev === 'string' ? parseInt(rev, 10) : rev;
      }
      
      if (!revision) {
        throw new Error('Unable to retrieve booking revision for cancellation');
      }
      
      console.log('cancelBooking: calling cancelBooking with id:', id, 'revision:', revision);
      
      // Wix's own cancellation fee/policy is never enforced — this app's
      // real billing lives in Zoho, not Wix, so Wix's policy engine is
      // always fully bypassed here regardless of the caller's waiveFee
      // choice. waiveFee only decides what happens on the Zoho side (see
      // the caller — the owner manually applies a fee there when not
      // waived; this call never charges anything through Wix).
      //
      // Sent here as `options`, but bookingsHandlers.ts's cancelBooking
      // handler nests this into flowControlSettings before calling Wix's
      // SDK — that's the field name Wix's real Cancel Booking schema
      // actually requires; a bare top-level `options` object is silently
      // ignored by the SDK, which was the real cause of BOOKING_POLICY_
      // VIOLATION errors persisting even with these flags set to true.
      const options: Record<string, boolean> = {
        waiveCancellationFee: true,
        ignoreCancellationPolicy: true,
        withRefund: true,
      };

      // Now cancel with the revision and options for cancellation policy
      await callAdminApi('cancelBooking', 'bookings', {
        id,
        revision,
        options
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches both bookings and blocked slots for the given date range and service IDs, and merges them as BookingItem[].
   * @param startDate Start of range (Date)
   * @param endDate End of range (Date)
   * @param serviceIds Array of service IDs to query for blocked slots
   * @returns Promise<Booking[]> (including blocked slots with resource info)
   */
  async getBookingsAndBlockedSlotsByDateRange(
    startDate: Date,
    endDate: Date,
    serviceIds: string[],
    maxResults?: number
  ): Promise<Booking[]> {

    // IMPORTANT: Wix stores booking times in UTC, but we need to query for bookings
    // that occur on specific LOCAL dates. We need to expand the query range by +/- 1 day
    // to catch bookings that cross midnight UTC but are on the correct local date.
    const queryStartDate = new Date(startDate);
    queryStartDate.setDate(queryStartDate.getDate() - 1);
    const queryEndDate = new Date(endDate);
    queryEndDate.setDate(queryEndDate.getDate() + 1);

    // Fallback: discover service IDs if caller cache/provider returned empty values.
    let effectiveServiceIds = serviceIds;
    if (!effectiveServiceIds || effectiveServiceIds.length === 0) {
      try {
        const servicesResponse = await callAdminApi('queryServices', 'services', {});
        const servicesItems = (servicesResponse as { _items?: Array<{ _id?: string; id?: string }>; items?: Array<{ _id?: string; id?: string }> })._items
          || (servicesResponse as { _items?: Array<{ _id?: string; id?: string }>; items?: Array<{ _id?: string; id?: string }> }).items
          || [];
        effectiveServiceIds = servicesItems
          .map((svc) => svc._id || svc.id || '')
          .filter((id): id is string => Boolean(id));
      } catch {
        effectiveServiceIds = [];
      }
    }

    console.log('[BlockedSlotsDebug] getBookingsAndBlockedSlotsByDateRange:start', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      inputServiceIds: serviceIds.length,
      effectiveServiceIds: effectiveServiceIds.length,
      maxResults: maxResults ?? null,
    });

    // 1. Fire bookings query and blocked slots query in parallel
    const blockedSlotsPromise = this.queryBlockedSlotsInternal({
      serviceIds: effectiveServiceIds,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
    // It is awaited further down; if the bookings query below fails first, this must not become an unhandled rejection
    blockedSlotsPromise.catch(() => undefined);

    const allBookings: Record<string, unknown>[] = [];
    let cursor: string | undefined;
    let hasMore = true;

    while (hasMore) {
      const queryParams: {
        filter?: Record<string, { $gte?: string; $lte?: string }>;
        limit?: number;
        cursorPaging?: { cursor: string };
      } = {};

      if (!cursor) {
        queryParams.filter = {
          startDate: { $lte: queryEndDate.toISOString() },
          endDate: { $gte: queryStartDate.toISOString() }
        };
        queryParams.limit = maxResults ?? 100;
      } else {
        queryParams.cursorPaging = { cursor };
      }

      const bookingsResult = await callAdminApi('queryBookings', 'bookings', queryParams);

      const pageBookings = (bookingsResult as {
        items?: Record<string, unknown>[],
        extendedBookings?: Record<string, unknown>[],
        cursors?: { next?: string, prev?: string },
        pagingMetadata?: {
          hasNext: boolean;
          cursors?: { next?: string };
        }
      })?.items || (bookingsResult as {
        items?: Record<string, unknown>[],
        extendedBookings?: Record<string, unknown>[],
        cursors?: { next?: string, prev?: string },
        pagingMetadata?: {
          hasNext: boolean;
          cursors?: { next?: string };
        }
      })?.extendedBookings || [];

      const pagingMetadata = (bookingsResult as {
        items?: Record<string, unknown>[],
        cursors?: { next?: string, prev?: string },
        pagingMetadata?: {
          hasNext: boolean;
          cursors?: { next?: string };
        }
      })?.pagingMetadata;

      allBookings.push(...pageBookings);

      hasMore = maxResults ? false : !!pagingMetadata?.hasNext;
      cursor = pagingMetadata?.cursors?.next;

      if (allBookings.length > 2000) break;
    }

    // 2. Await blocked slots (likely already resolved) + enrich avatars in parallel
    const mappedBookings = allBookings.map(mapWixBookingToBooking);

    const [enrichedBookings, blockedSlotsResult] = await Promise.all([
      enrichBookingsWithContactAvatars(mappedBookings),
      blockedSlotsPromise,
    ]);

    const processedBlockedItems = (blockedSlotsResult as { items?: unknown[] })?.items || [];
    const mappedBlocked = this.convertAgendaItemsToBookings(processedBlockedItems as AgendaItem[], new Map());

    const blockedMeta = (blockedSlotsResult as {
      metadata?: { total?: number; sources?: { availability?: number; calendarEvents?: number; schedules?: number } };
    })?.metadata;

    console.log('[BlockedSlotsDebug] getBookingsAndBlockedSlotsByDateRange:result', {
      bookingsCount: enrichedBookings.length,
      blockedAgendaCount: processedBlockedItems.length,
      blockedMappedCount: mappedBlocked.length,
      blockedMeta: blockedMeta || null,
    });

    const blockedSamples = mappedBlocked.slice(0, 10).map((b) => {
      const start = b.startTime instanceof Date ? b.startTime : new Date(b.startTime);
      const end = b.endTime instanceof Date ? b.endTime : new Date(b.endTime);
      return {
        id: b.id,
        title: b.title,
        startTimeUtc: start.toISOString(),
        endTimeUtc: end.toISOString(),
        startTimeLocal: start.toString(),
        endTimeLocal: end.toString(),
        date: b.date,
        serviceType: b.serviceType,
      };
    });

    console.log('[BlockedSlotsDebug] getBookingsAndBlockedSlotsByDateRange:blockedSamples', blockedSamples);
    console.log('[BlockedSlotsDebug] getBookingsAndBlockedSlotsByDateRange:blockedSamples:json', JSON.stringify(blockedSamples));

    return [...enrichedBookings, ...mappedBlocked];
  }

  /**
   * One page of bookings (all statuses, cancelled included) that start before
   * `before`, newest first, via Wix cursor paging. Pass the returned `nextCursor`
   * to continue. Wix's default order is createdDate DESC, so the sort must be
   * explicit or pages come back in creation order and skip older sessions.
   */
  async getBookingsPageBefore(
    before: Date,
    cursor?: string,
    limit = 30
  ): Promise<{ bookings: Booking[]; nextCursor?: string; hasNext: boolean }> {
    const result = await callAdminApi('queryBookings', 'bookings', cursor
      ? { cursorPaging: { cursor } }
      : {
          filter: { startDate: { $lt: before.toISOString() } },
          sort: [{ fieldName: 'startDate', order: 'DESC' }],
          limit,
        }) as {
      items?: Record<string, unknown>[];
      extendedBookings?: Record<string, unknown>[];
      pagingMetadata?: { hasNext?: boolean; cursors?: { next?: string } };
    };
    const items = result?.items || result?.extendedBookings || [];
    const nextCursor = result?.pagingMetadata?.cursors?.next;
    const bookings = await enrichBookingsWithContactAvatars(items.map(mapWixBookingToBooking));
    return { bookings, nextCursor, hasNext: !!result?.pagingMetadata?.hasNext && !!nextCursor };
  }

  async getBlockedSlotsByDateRange(
    startDate: Date,
    endDate: Date,
    serviceIds: string[]
  ): Promise<Booking[]> {
    let effectiveServiceIds = serviceIds;
    if (!effectiveServiceIds || effectiveServiceIds.length === 0) {
      try {
        const servicesResponse = await callAdminApi('queryServices', 'services', {});
        const servicesItems = (servicesResponse as { _items?: Array<{ _id?: string; id?: string }>; items?: Array<{ _id?: string; id?: string }> })._items
          || (servicesResponse as { _items?: Array<{ _id?: string; id?: string }>; items?: Array<{ _id?: string; id?: string }> }).items
          || [];
        effectiveServiceIds = servicesItems
          .map((svc) => svc._id || svc.id || '')
          .filter((id): id is string => Boolean(id));
      } catch {
        effectiveServiceIds = [];
      }
    }

    const blockedSlotsResult = await this.queryBlockedSlotsInternal({
      serviceIds: effectiveServiceIds,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });

    const processedBlockedItems = (blockedSlotsResult as { items?: unknown[] })?.items || [];
    return this.convertAgendaItemsToBookings(processedBlockedItems as AgendaItem[], new Map());
  }

  /**
   * Process blocked slots from availability calendar
   */
  private processBlockedSlots(
    blockedSlots: unknown[],
    resourceMap: Map<string, Resource>,
    _staffMap: Map<string, StaffMember>,
    _scheduleMap: Map<string, Schedule>
  ): AgendaItem[] {


    const blockedSlotsTyped = blockedSlots as BlockedSlot[];

    // Wix availability can include slot-level states under nestedSlots.
    // Promote nested slot entries into top-level candidates so blocked detection sees them.
    const blockedCandidates: BlockedSlot[] = [];
    blockedSlotsTyped.forEach((entry) => {
      blockedCandidates.push(entry);

      if (!Array.isArray(entry.nestedSlots) || entry.nestedSlots.length === 0) {
        return;
      }

      entry.nestedSlots.forEach((nested) => {
        if (!nested || typeof nested !== 'object') return;

        const nestedObj = nested as Record<string, unknown>;
        const nestedSlot = (nestedObj.slot as Record<string, unknown>) || {};
        const parentSlot = (entry.slot as unknown as Record<string, unknown>) || {};

        const startDate = nestedSlot.startDate || nestedObj.startDate || parentSlot.startDate;
        const endDate = nestedSlot.endDate || nestedObj.endDate || parentSlot.endDate;
        const scheduleId = nestedSlot.scheduleId || nestedObj.scheduleId || parentSlot.scheduleId;
        const serviceId = nestedSlot.serviceId || nestedObj.serviceId || parentSlot.serviceId;
        const resource = (nestedSlot.resource as Record<string, unknown> | undefined)
          || (nestedObj.resource as Record<string, unknown> | undefined)
          || (parentSlot.resource as Record<string, unknown> | undefined);

        const candidate: BlockedSlot = {
          slot: {
            serviceId: typeof serviceId === 'string' ? serviceId : '',
            scheduleId: typeof scheduleId === 'string' ? scheduleId : '',
            startDate: typeof startDate === 'string' ? startDate : '',
            endDate: typeof endDate === 'string' ? endDate : '',
            resource: resource ? {
              _id: typeof resource._id === 'string' ? resource._id : undefined,
              name: typeof resource.name === 'string' ? resource.name : undefined,
            } : undefined,
          },
          bookable: typeof nestedObj.bookable === 'boolean'
            ? nestedObj.bookable
            : entry.bookable,
          totalSpots: typeof nestedObj.totalSpots === 'number'
            ? nestedObj.totalSpots
            : entry.totalSpots,
          openSpots: typeof nestedObj.openSpots === 'number'
            ? nestedObj.openSpots
            : entry.openSpots,
          bookingPolicyViolations: ((nestedObj.bookingPolicyViolations as Record<string, boolean> | undefined)
            || entry.bookingPolicyViolations),
          nestedSlots: [],
          serviceId: typeof serviceId === 'string' ? serviceId : undefined,
        };

        blockedCandidates.push(candidate);
      });
    });

    // Availability responses include many non-bookable entries that are not true blocks
    // (for example booking-window policy violations). Keep only explicit blocking signals.
    const policyOnlyViolations = new Set(['tooEarlyToBook', 'tooLateToBook', 'bookOnlineDisabled']);
    const explicitBlockingKeys = new Set([
      'blockedTime',
      'resourceBlocked',
      'staffUnavailable',
      'customAvailability',
      'outsideWorkingHours',
      'scheduleClosed',
      'slotUnavailable'
    ]);
    const seenKeys = new Set<string>();

    let rejectedNoSlot = 0;
    let rejectedInvalidTime = 0;
    let rejectedBookable = 0;
    let rejectedPolicyOnly = 0;
    let rejectedNoPositiveSignal = 0;
    let rejectedDuplicate = 0;
    let acceptedNoViolations = 0;
    let acceptedWithBlockingViolation = 0;
    let acceptedNoCapacity = 0;

    const trueViolationHistogram: Record<string, number> = {};
    const bookableHistogram: Record<string, number> = {};
    const spotsHistogram: Record<string, number> = {};

    const filteredForBlocked = blockedCandidates.filter(s => {
      const slot = s.slot;
      if (!slot) {
        rejectedNoSlot++;
        return false;
      }
      const parsedStart = this.parseEventTime(slot.startDate);
      const parsedEnd = this.parseEventTime(slot.endDate);
      if (!parsedStart || !parsedEnd) {
        rejectedInvalidTime++;
        return false;
      }
      // Some Wix responses omit `bookable` on blocked slots. Only reject explicitly bookable entries.
      const bookableKey = String(s.bookable);
      bookableHistogram[bookableKey] = (bookableHistogram[bookableKey] || 0) + 1;

      const spotsKey = `${String(s.openSpots)}|${String(s.totalSpots)}`;
      spotsHistogram[spotsKey] = (spotsHistogram[spotsKey] || 0) + 1;

      if (s.bookable === true) {
        rejectedBookable++;
        return false;
      }

      const violationsObj = (s.bookingPolicyViolations || {}) as Record<string, unknown>;
      const trueViolations = Object.entries(violationsObj)
        .filter(([, value]) => value === true || value === 'true' || value === 1)
        .map(([key]) => key);

      trueViolations.forEach((key) => {
        trueViolationHistogram[key] = (trueViolationHistogram[key] || 0) + 1;
      });

      const noCapacity = typeof s.totalSpots === 'number' && typeof s.openSpots === 'number'
        ? s.totalSpots > 0 && s.openSpots === 0
        : false;
      const zeroCapacity = s.totalSpots === 0 && s.openSpots === 0;

      // Keep explicit no-capacity non-bookable slots as blocked candidates.
      if (trueViolations.length === 0 && (noCapacity || zeroCapacity) && s.bookable === false) {
        const resourceId = slot.resource?._id || '';
        const dedupeKey = `${slot.scheduleId || ''}|${parsedStart.toISOString()}|${parsedEnd.toISOString()}|${resourceId}`;
        if (seenKeys.has(dedupeKey)) {
          rejectedDuplicate++;
          return false;
        }
        seenKeys.add(dedupeKey);
        acceptedNoCapacity++;
        return true;
      }

      if (trueViolations.length === 0) {
        rejectedNoPositiveSignal++;
        return false;
      }

      const hasBlockingViolation = trueViolations.some((key) => {
        if (policyOnlyViolations.has(key)) return false;
        if (explicitBlockingKeys.has(key)) return true;

        const normalized = key.toLowerCase();
        return normalized.includes('block')
          || normalized.includes('unavail')
          || normalized.includes('busy')
          || normalized.includes('closed')
          || normalized.includes('timeoff')
          || normalized.includes('workinghour');
      });
      if (!hasBlockingViolation) {
        rejectedPolicyOnly++;
        return false;
      }

      const resourceId = slot.resource?._id || '';
      const dedupeKey = `${slot.scheduleId || ''}|${parsedStart.toISOString()}|${parsedEnd.toISOString()}|${resourceId}`;
      if (seenKeys.has(dedupeKey)) {
        rejectedDuplicate++;
        return false;
      }
      seenKeys.add(dedupeKey);
      acceptedWithBlockingViolation++;

      return true;
    });

    console.log('[BlockedSlotsDebug] processBlockedSlots:summary', {
      inputCount: blockedSlotsTyped.length,
      candidateCount: blockedCandidates.length,
      outputCount: filteredForBlocked.length,
      rejectedNoSlot,
      rejectedInvalidTime,
      rejectedBookable,
      rejectedPolicyOnly,
      rejectedNoPositiveSignal,
      rejectedDuplicate,
      acceptedNoViolations,
      acceptedWithBlockingViolation,
      acceptedNoCapacity,
      trueViolationHistogram,
      bookableHistogram,
      spotsHistogram,
    });
    console.log('[BlockedSlotsDebug] processBlockedSlots:summary:json', JSON.stringify({
      inputCount: blockedSlotsTyped.length,
      candidateCount: blockedCandidates.length,
      outputCount: filteredForBlocked.length,
      rejectedNoSlot,
      rejectedInvalidTime,
      rejectedBookable,
      rejectedPolicyOnly,
      rejectedNoPositiveSignal,
      rejectedDuplicate,
      acceptedNoViolations,
      acceptedWithBlockingViolation,
      acceptedNoCapacity,
      trueViolationHistogram,
      bookableHistogram,
      spotsHistogram,
    }));

    const sampleEntries = blockedCandidates.slice(0, 5).map((entry, index) => ({
      index,
      bookable: entry.bookable,
      totalSpots: entry.totalSpots,
      openSpots: entry.openSpots,
      scheduleId: entry.slot?.scheduleId,
      startType: typeof entry.slot?.startDate,
      endType: typeof entry.slot?.endDate,
      startDate: entry.slot?.startDate,
      endDate: entry.slot?.endDate,
      violations: (entry.bookingPolicyViolations || {}) as Record<string, unknown>,
    }));

    console.log('[BlockedSlotsDebug] processBlockedSlots:samples', sampleEntries);
  console.log('[BlockedSlotsDebug] processBlockedSlots:samples:json', JSON.stringify(sampleEntries));

    // Map blocked slots to agenda items
    return filteredForBlocked.map((s, idx) => {
      const slot = s.slot || {} as Record<string, unknown>;
      const normalizedStart = this.parseEventTime(slot.startDate);
      const normalizedEnd = this.parseEventTime(slot.endDate);

      // Get associated resource and staff info
      const resourceId = (slot.resource as Record<string, unknown>)?._id as string | undefined;
      const resourceObj = resourceId ? resourceMap.get(resourceId) : undefined;

      const agendaItem = {
        _id: `blocked-slot-${(slot.scheduleId as string) || idx}`,
        type: 'BLOCKED_TIME' as const,
        scheduleId: slot.scheduleId as string | undefined,
        startDate: normalizedStart ? normalizedStart.toISOString() : undefined,
        endDate: normalizedEnd ? normalizedEnd.toISOString() : undefined,
        isBlocked: true,
        isAppointment: false,
        resourceObj,
        staffMember: null, // BlockedSlot doesn't include staff member info
        slot: {
          startDate: normalizedStart ? normalizedStart.toISOString() : undefined,
          endDate: normalizedEnd ? normalizedEnd.toISOString() : undefined,
          staffMember: null, // BlockedSlot doesn't include staff member info
          resource: resourceObj ? {
            _id: resourceObj._id,
            type: resourceObj.type || 'RESOURCE'
          } : null
        }
      } as AgendaItem;

      return agendaItem;
    });
  }

  /**
   * Process calendar events (including staff-level blocks)
   */
  private processCalendarEvents(
    calendarEvents: unknown[],
    resourceMap: Map<string, Resource>,
    staffMap: Map<string, StaffMember>,
    scheduleMap: Map<string, Schedule>
  ): AgendaItem[] {

    const typedEvents = calendarEvents as CalendarEvent[];

    const eventTypeHistogram: Record<string, number> = {};
    const transparencyHistogram: Record<string, number> = {};
    typedEvents.forEach((event) => {
      const t = String(event.type || 'UNKNOWN');
      const tr = String(event.transparency || 'NONE');
      eventTypeHistogram[t] = (eventTypeHistogram[t] || 0) + 1;
      transparencyHistogram[tr] = (transparencyHistogram[tr] || 0) + 1;
    });
    console.log('[BlockedSlotsDebug] processCalendarEvents:histograms', {
      inputCount: typedEvents.length,
      eventTypeHistogram,
      transparencyHistogram,
    });

    // Filter out APPOINTMENT type events as they should be regular bookings, not blocked time
    const filteredEvents = typedEvents.filter(event => {
      const isAppointment = event.type === 'APPOINTMENT';
      if (isAppointment) {
        return false;
      }
      return true;
    });

    const mapped = filteredEvents.flatMap((event, idx) => {
      const eventRecord = event as Record<string, unknown>;

      // Wix event payloads can vary by endpoint/version; support common start/end aliases.
      const rawStart = eventRecord.start
        ?? eventRecord.startDate
        ?? eventRecord.from
        ?? eventRecord.startTime;
      const rawEnd = eventRecord.end
        ?? eventRecord.endDate
        ?? eventRecord.to
        ?? eventRecord.endTime;

      const startTime = this.parseEventTime(rawStart);
      let endTime = this.parseEventTime(rawEnd);

      // If we cannot determine a start time, this event cannot be rendered on the time grid.
      if (!startTime) {
        return [];
      }

      // If end is missing/invalid but start exists, render a visible fallback block.
      if (startTime && (!endTime || endTime.getTime() <= startTime.getTime())) {
        endTime = new Date(startTime.getTime() + (30 * 60 * 1000));
      }

      // Use proper Wix API fields for staff association
      let associatedStaff: StaffMember | null = null;
      let associatedResource: Resource | null = null;

      // 1. Check for direct staffMember field (primary method)
      if (event.staffMember) {
        if (typeof event.staffMember === 'string') {
          // staffMember is an ID
          associatedStaff = staffMap.get(event.staffMember) || null;
        } else if (typeof event.staffMember === 'object' && event.staffMember !== null && '_id' in event.staffMember) {
          // staffMember is an object with _id
          const staffMemberObj = event.staffMember as { _id: string };
          associatedStaff = staffMap.get(staffMemberObj._id) || null;
        }
      }

      // 2. Check for resourceId field (secondary method)
      if (!associatedStaff && event.resourceId && typeof event.resourceId === 'string') {
        // First check if resourceId maps to a staff member
        associatedStaff = staffMap.get(event.resourceId) || null;

        if (!associatedStaff) {
          // If not a staff member, check if it's a resource
          associatedResource = resourceMap.get(event.resourceId) || null;
        }
      }

      // 3. Check for externalScheduleId field (Wix-specific field for resource association)
      if (!associatedStaff && !associatedResource && event.externalScheduleId && typeof event.externalScheduleId === 'string') {
        // Check if externalScheduleId maps to a staff member's resourceId
        for (const staff of staffMap.values()) {
          if (staff.resourceId === event.externalScheduleId) {
            associatedStaff = staff;
            break;
          }
        }

        // If not found as staff, check if it's a resource
        if (!associatedStaff) {
          associatedResource = resourceMap.get(event.externalScheduleId) || null;
        }
      }

      // 4. Check for scheduleId field (tertiary method)
      if (!associatedStaff && !associatedResource && event.scheduleId && typeof event.scheduleId === 'string') {
        const schedule = scheduleMap.get(event.scheduleId);
        // Wix's Schedule has no `resourceId` field — for a staff member's own
        // schedule, `externalId` is what equals that staff's resourceId.
        const scheduleExternalId = schedule && typeof schedule.externalId === 'string' ? schedule.externalId : undefined;
        if (scheduleExternalId) {
          // Check if the schedule's resource is a staff member
          associatedStaff = staffMap.get(scheduleExternalId) || null;

          if (!associatedStaff) {
            // If not a staff member, check if it's a resource
            associatedResource = resourceMap.get(scheduleExternalId) || null;
          }
        }
      }

      // 4. Fallback to resources array only if no direct fields found
      if (!associatedStaff && !associatedResource && event.resources && event.resources.length > 0) {
        // Process first resource in the array
        const firstResource = event.resources[0];
        if (firstResource && firstResource._id) {
          // Check if it's a staff member first
          associatedStaff = staffMap.get(firstResource._id) || null;

          if (!associatedStaff) {
            // If not a staff member, check if it's a resource
            associatedResource = resourceMap.get(firstResource._id) || null;
          }
        }
      }

      const agendaItem = {
        _id: event._id || `event-${idx}`,
        type: 'BLOCKED_TIME', // All filtered events are blocked time
        originalType: event.type,
        title: event.title, // Include the original event title from Wix
        notes: typeof eventRecord.notes === 'string' ? eventRecord.notes : undefined,
        revision: typeof eventRecord.revision === 'string' ? eventRecord.revision : undefined,
        recurrenceType: typeof eventRecord.recurrenceType === 'string' ? eventRecord.recurrenceType : undefined,
        recurringEventId: typeof eventRecord.recurringEventId === 'string' ? eventRecord.recurringEventId : undefined,
        recurrenceRule: (() => {
          const rule = eventRecord.recurrenceRule as Record<string, unknown> | undefined;
          if (!rule || typeof rule !== 'object') return undefined;
          const until = rule.until as Record<string, unknown> | undefined;
          return {
            frequency: typeof rule.frequency === 'string' ? rule.frequency : undefined,
            interval: typeof rule.interval === 'number' ? rule.interval : undefined,
            days: Array.isArray(rule.days) ? rule.days.filter((d): d is string => typeof d === 'string') : undefined,
            until: typeof until?.localDate === 'string' ? until.localDate : undefined,
          };
        })(),
        scheduleId: event.scheduleId,
        externalScheduleId: event.externalScheduleId,
        startDate: startTime && !isNaN(startTime.getTime()) ? startTime.toISOString() : undefined,
        endDate: endTime && !isNaN(endTime.getTime()) ? endTime.toISOString() : undefined,
        transparency: event.transparency,
        isBlocked: true, // All filtered events are blocks
        isAppointment: false, // We filtered out appointments
        totalCapacity: event.totalCapacity,
        remainingCapacity: event.remainingCapacity,
        resources: event.resources || [],
        staffMember: associatedStaff ? {
          _id: associatedStaff._id,
          name: associatedStaff.name,
          resourceId: associatedStaff.resourceId
        } : null,
        resourceObj: associatedResource,
        slot: {
          startDate: startTime && !isNaN(startTime.getTime()) ? startTime.toISOString() : undefined,
          endDate: endTime && !isNaN(endTime.getTime()) ? endTime.toISOString() : undefined,
          staffMember: associatedStaff ? {
            _id: associatedStaff._id,
            name: associatedStaff.name
          } : null,
          resource: associatedResource ? {
            _id: associatedResource._id,
            type: associatedResource.type || 'RESOURCE'
          } : null
        }
      } as AgendaItem;

      return [agendaItem];
    });

    console.log('[BlockedSlotsDebug] processCalendarEvents:result', {
      filteredEvents: filteredEvents.length,
      mappedCount: mapped.length,
    });

    return mapped;
  }

  /**
   * Parse event time that can be either a string or an object
   */
  private parseEventTime(timeData: unknown): Date | null {
    if (!timeData) return null;

    let d: Date | null = null;

    if (timeData instanceof Date) {
      d = timeData;
    } else if (typeof timeData === 'object' && timeData !== null && '$date' in (timeData as Record<string, unknown>)) {
      const ts = (timeData as { $date: number | string }).$date;
      d = new Date(typeof ts === 'string' ? parseInt(ts, 10) : ts);
    }

    if (!d && typeof timeData === 'string') {
      d = new Date(timeData);
    } else if (!d && typeof timeData === 'number') {
      d = new Date(timeData);
    } else if (!d && typeof timeData === 'object' && timeData !== null && 'utcDate' in timeData) {
      const timeObj = timeData as { utcDate: unknown; localDate?: string };
      if (timeObj.utcDate instanceof Date) {
        d = timeObj.utcDate;
      } else if (typeof timeObj.utcDate === 'string') {
        d = new Date(timeObj.utcDate);
      } else if (timeObj.utcDate && typeof timeObj.utcDate === 'object' && '$date' in (timeObj.utcDate as Record<string, unknown>)) {
        const ts = (timeObj.utcDate as { $date: number | string }).$date;
        d = new Date(typeof ts === 'string' ? parseInt(ts, 10) : ts);
      } else if (typeof timeObj.localDate === 'string') {
        // Wix Calendar often returns localDate with timeZone and empty utcDate object.
        d = new Date(timeObj.localDate);
      } else if (timeObj.localDate && typeof timeObj.localDate === 'object' && '$date' in (timeObj.localDate as Record<string, unknown>)) {
        const ts = (timeObj.localDate as unknown as { $date: number | string }).$date;
        d = new Date(typeof ts === 'string' ? parseInt(ts, 10) : ts);
      }
    } else if (!d && typeof timeData === 'object' && timeData !== null) {
      const timeObj = timeData as Record<string, unknown>;

      if (typeof timeObj.isoString === 'string') {
        d = new Date(timeObj.isoString);
      } else if (typeof timeObj.dateTime === 'string') {
        d = new Date(timeObj.dateTime);
      } else if (typeof timeObj.localDateTime === 'string') {
        d = new Date(timeObj.localDateTime);
      } else if (typeof timeObj.localDate === 'string') {
        d = new Date(timeObj.localDate);
      } else if (timeObj.localDate && typeof timeObj.localDate === 'object' && '$date' in (timeObj.localDate as Record<string, unknown>)) {
        const ts = (timeObj.localDate as unknown as { $date: number | string }).$date;
        d = new Date(typeof ts === 'string' ? parseInt(ts, 10) : ts);
      } else if (typeof timeObj.startDate === 'string') {
        d = new Date(timeObj.startDate);
      } else if (typeof timeObj.endDate === 'string') {
        d = new Date(timeObj.endDate);
      } else if (typeof timeObj.seconds === 'number') {
        d = new Date(timeObj.seconds * 1000);
      }
    }

    return d && !isNaN(d.getTime()) ? d : null;
  }

  /**
   * Convert agenda items to Booking format for calendar display
   */
  private convertAgendaItemsToBookings(
    agendaItems: AgendaItem[],
    resourceMap: Map<string, Resource>
  ): Booking[] {
    const seenBlockedKeys = new Set<string>();

    return agendaItems.map((item, idx) => {
      const parsedStart = this.parseEventTime(item.startDate);
      const parsedEnd = this.parseEventTime(item.endDate);

      const startTime = parsedStart || new Date();
      let endTime = parsedEnd || new Date(startTime.getTime() + (30 * 60 * 1000));
      if (endTime.getTime() <= startTime.getTime()) {
        endTime = new Date(startTime.getTime() + (30 * 60 * 1000));
      }

      // Generate a stable, unique id
      let baseId = '';
      if (item._id) {
        baseId = `blocked-${item._id}`;
      } else if (item.scheduleId) {
        baseId = `blocked-${item.scheduleId}`;
      } else if (item.startDate && item.endDate) {
        baseId = `blocked-${item.startDate}-${item.endDate}`;
      } else {
        baseId = `blocked-fallback-${idx}`;
      }

      // Ensure uniqueness
      let uniqueId = baseId;
      let dupeCounter = 1;
      while (seenBlockedKeys.has(uniqueId)) {
        uniqueId = `${baseId}-${dupeCounter}`;
        dupeCounter++;
      }
      seenBlockedKeys.add(uniqueId);

      // Get resource info
      const resourceObj = item.resourceObj || (item.slot?.resource?._id ? resourceMap.get(item.slot.resource._id) : undefined);
      const staffName = item.staffMember?.name || item.slot?.staffMember?.name || '';

      // Determine title based on type and available information
      let title = 'Blocked Time';
      let serviceType: ServiceType = 'Other';

      // First, check if we have a meaningful title from the Wix event
      if (item.title && item.title.trim() !== '' && !item.title.toLowerCase().includes('default')) {
        // Use the original Wix event title, but enhance it with staff/resource info if available
        if (staffName && !item.title.includes(staffName)) {
          title = `${staffName} - ${item.title}`;
        } else if (resourceObj?.name && !item.title.includes(resourceObj.name)) {
          title = `${resourceObj.name} - ${item.title}`;
        } else {
          title = item.title;
        }
        serviceType = 'Deep Cleaning'; // Assume these are staff-related when they have custom titles
      } else if (item.isAppointment) {
        title = 'Appointment';
        serviceType = 'Recurring Cleaning'; // Appointments are likely service bookings
      } else if (item.type === 'BLOCKED_TIME') {
        if (staffName) {
          title = `${staffName} - Unavailable`;
          serviceType = 'Deep Cleaning'; // Staff blocks
        } else if (resourceObj?.name) {
          title = `${resourceObj.name} - Blocked`;
          serviceType = 'Other';
        } else {
          // Generic blocked time without specific staff/resource
          title = 'Schedule Block';
          serviceType = 'Other';
        }
      }

      // If we have calendar event data, try to extract more meaningful info
      if (item.originalType && !item.title) { // Only apply if we don't already have a meaningful title
        if (item.originalType === 'WORKING_HOURS') {
          title = staffName ? `${staffName} - Working Hours` : 'Working Hours';
          serviceType = 'Recurring Cleaning';
        } else if (item.originalType === 'TIME_OFF') {
          title = staffName ? `${staffName} - Time Off` : 'Time Off';
          serviceType = 'Other';
        } else if (item.originalType === 'BREAK') {
          title = staffName ? `${staffName} - Break` : 'Break';
          serviceType = 'Other';
        } else if (item.originalType === 'DEFAULT') {
          // For DEFAULT type events, try to use staff/resource name if available
          if (staffName) {
            title = `${staffName} - Busy`;
            serviceType = 'Deep Cleaning';
          } else if (resourceObj?.name) {
            title = `${resourceObj.name} - Busy`;
            serviceType = 'Commercial Cleaning';
          } else {
            title = 'Busy Time';
            serviceType = 'Other';
          }
        }
      }

      // If we still have a generic title but have actual staff/resource names, use them
      if ((title === 'Blocked Time' || title === 'Schedule Block') && (staffName || resourceObj?.name)) {
        if (staffName) {
          title = `${staffName} - Unavailable`;
          serviceType = 'Deep Cleaning';
        } else if (resourceObj?.name) {
          title = `${resourceObj.name} - Blocked`;
          serviceType = 'Commercial Cleaning';
        }
      }

      // Add time information to title for better context
      const timeStr = `${startTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })} - ${endTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;

      // Extract local date from the startTime Date object
      const localYear = startTime.getFullYear();
      const localMonth = String(startTime.getMonth() + 1).padStart(2, '0');
      const localDay = String(startTime.getDate()).padStart(2, '0');
      const localDateStr = `${localYear}-${localMonth}-${localDay}`;

      const finalBooking = {
        id: uniqueId,
        title: `${title} (${timeStr})`,
        type: 'blocked' as const,
        startTime,
        endTime,
        date: localDateStr, // Use local date, not UTC date
        serviceType,
        status: 'confirmed' as const,
        notes: item.notes || '',
        location: '', // AgendaItem slot doesn't have location info
        contactId: '',
        contact: {
          id: '',
          name: staffName || resourceObj?.name || '',
          email: resourceObj?.email || '',
          phone: resourceObj?.phone || '',
          address: '',
          avatarUrl: resourceObj?.avatarUrl || ''
        },
        staff: staffName,
        resource: resourceObj?._id || item.slot?.resource?._id || '',
        transparency: item.transparency, // Pass through transparency info for frontend styling
        price: 0,
        isCompleted: false,
        duration: Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60)), // Duration in minutes
        frequency: 'one-time' as const,
        bookingId: '',
        createdAt: startTime,
        updatedAt: endTime,
        // Add additional metadata for advanced UI
        resourceObj,
        agendaItem: item
      };

      return finalBooking;
    });
  }

  /**
   * Process schedules for blocked time periods (working hours exceptions, time-off, etc.)
   */
  private processSchedulesForBlockedTime(
    schedulesData: unknown[],
    resourceMap: Map<string, Resource>,
    staffMap: Map<string, StaffMember>,
    startDate: Date,
    endDate: Date
  ): AgendaItem[] {
    
    const agendaItems: AgendaItem[] = [];
    
    schedulesData.forEach((schedule, _idx) => {
      const typedSchedule = schedule as Record<string, unknown>;
      const scheduleName = typedSchedule.name as string;
      // Wix's Schedule has no `resourceId` field — `externalId` is what equals
      // the resource's id for a staff member's own schedule.
      const resourceId = typedSchedule.externalId as string;
      
      // Find associated staff member for this schedule
      let associatedStaff: StaffMember | null = null;
      let associatedResource: Resource | null = null;
      
      if (resourceId) {
        associatedStaff = staffMap.get(resourceId) || null;
        if (!associatedStaff) {
          associatedResource = resourceMap.get(resourceId) || null;
        }
      }
      
      // Process schedule blocked intervals from multiple common Wix shapes.
      const periodBuckets = [
        { key: 'timeOff', label: 'Time Off' },
        { key: 'timeOffPeriods', label: 'Time Off' },
        { key: 'unavailableIntervals', label: 'Unavailable' },
        { key: 'exceptions', label: 'Unavailable' },
        { key: 'blackoutPeriods', label: 'Blocked' },
        { key: 'blockedTime', label: 'Blocked' },
      ];

      periodBuckets.forEach(({ key, label }) => {
        const rawPeriods = typedSchedule[key] as unknown;
        if (!rawPeriods) return;

        const periods = Array.isArray(rawPeriods)
          ? rawPeriods
          : (typeof rawPeriods === 'object' && rawPeriods !== null ? [rawPeriods] : []);

        periods.forEach((periodRaw, periodIdx) => {
          if (!periodRaw || typeof periodRaw !== 'object') return;
          const period = periodRaw as Record<string, unknown>;

          const startRaw = period.startDate || period.start || period.startTime || period.from;
          const endRaw = period.endDate || period.end || period.endTime || period.to;

          const parsedStart = this.parseEventTime(startRaw);
          const parsedEnd = this.parseEventTime(endRaw);
          if (!parsedStart || !parsedEnd) return;

          // Check if this blocked period overlaps requested range
          if (!(parsedStart <= endDate && parsedEnd >= startDate)) return;

          const startIso = parsedStart.toISOString();
          const endIso = parsedEnd.toISOString();

          const agendaItem = {
            _id: `schedule-${key}-${typedSchedule._id || 'unknown'}-${periodIdx}`,
            type: 'BLOCKED_TIME' as const,
            originalType: key === 'timeOff' || key === 'timeOffPeriods' ? 'TIME_OFF' : 'DEFAULT',
            title: `${scheduleName || 'Schedule'} - ${label}`,
            scheduleId: typedSchedule._id as string,
            startDate: startIso,
            endDate: endIso,
            isBlocked: true,
            isAppointment: false,
            staffMember: associatedStaff ? {
              _id: associatedStaff._id,
              name: associatedStaff.name,
              resourceId: associatedStaff.resourceId,
            } : null,
            resourceObj: associatedResource,
            slot: {
              startDate: startIso,
              endDate: endIso,
              staffMember: associatedStaff ? {
                _id: associatedStaff._id,
                name: associatedStaff.name,
              } : null,
              resource: associatedResource ? {
                _id: associatedResource._id,
                type: associatedResource.type || 'RESOURCE',
              } : null,
            },
          } as AgendaItem;

          agendaItems.push(agendaItem);
        });
      });
    });
    
    return agendaItems;
  }

  /**
   * Internal method for comprehensive blocked slots query (Firebase function logic)
   * This implements the complex parallel API processing without circular dependencies
   */
  private async queryBlockedSlotsInternal(params: {
    serviceIds: string[];
    startDate: string;
    endDate: string;
  }): Promise<{
    items: AgendaItem[];
    resources: unknown;
    staff: unknown;
    schedules: unknown;
    metadata: {
      total: number;
      sources: {
        availability: number;
        calendarEvents: number;
        schedules: number;
      };
    };
  }> {
    try {
      console.log('[BlockedSlotsDebug] queryBlockedSlotsInternal:start', {
        serviceIds: params.serviceIds.length,
        startDate: params.startDate,
        endDate: params.endDate,
      });

      // Call the comprehensive blocked slots handler
      const comprehensiveResult = await callAdminApi('queryBlockedSlots', 'bookings', {
        serviceIds: params.serviceIds,
        startDate: params.startDate,
        endDate: params.endDate
      });

      // Extract data from comprehensive result
      const comprehensiveData = comprehensiveResult as {
        availabilityResults?: unknown[]; // Raw availability results from handler
        calendarEvents?: unknown; // Calendar events from handler (could be object or array)
        // Parameters for API to execute remaining queries
        serviceIds?: string[];
        startDate?: string;
        endDate?: string;
      };

      // Execute resources, staff, and schedules queries in API layer (moved from handler)
      const [resourcesQueryResult, staffQueryResult, schedulesQueryResult] = await Promise.all([
        callAdminApi('queryResources', 'bookings', {}),
        callAdminApi('queryStaffMembers', 'bookings', {}),
        callAdminApi('querySchedules', 'bookings', {})
      ]);
      const allResources = resourcesQueryResult;
      const allStaffMembers = staffQueryResult;
      const allSchedules = schedulesQueryResult;

      // Step 1: Extract availability entries from raw results (moved from handler)
      let availabilityData: unknown[] = [];
      if (comprehensiveData.availabilityResults && Array.isArray(comprehensiveData.availabilityResults)) {
        comprehensiveData.availabilityResults.forEach((availRes: unknown) => {
          const result = availRes as { availabilityEntries?: unknown[] };
          if (result && Array.isArray(result.availabilityEntries)) {
            availabilityData.push(...result.availabilityEntries);
          }
        });
      }

      console.log('[BlockedSlotsDebug] queryBlockedSlotsInternal:raw', {
        availabilityResultsCount: Array.isArray(comprehensiveData.availabilityResults) ? comprehensiveData.availabilityResults.length : 0,
        availabilityEntriesCount: availabilityData.length,
      });

      // Step 2: Process resources from API query result (moved from handler)
      let resourcesResult: unknown[] = [];
      if (allResources) {
        const resourcesData = allResources as { items?: unknown[] };
        resourcesResult = resourcesData?.items || [];
      }
      
      // Step 3: Process staff members from API query result (moved from handler)
      let staffMembersResult: unknown[] = [];
      
      // Use staff data from API query instead of handler
      if (allStaffMembers) {
        // Check if it's already an array (direct items) or wrapped in object
        if (Array.isArray(allStaffMembers)) {
          staffMembersResult = allStaffMembers;
        } else {
          // Check for both items and _items formats
          const staffData = allStaffMembers as { items?: unknown[]; _items?: unknown[] };
          staffMembersResult = staffData?.items || staffData?._items || [];
        }
      } else {
        // No staff data found from API
      }
      
      // Step 4: Process calendar events from raw result (moved from handler)
      let calendarEventsResult: unknown[] = [];
      if (comprehensiveData.calendarEvents) {
        // Check if it's already an array (direct items) or wrapped in object
        if (Array.isArray(comprehensiveData.calendarEvents)) {
          calendarEventsResult = comprehensiveData.calendarEvents;
        } else {
          // Check for both items and _items formats
          const calendarData = comprehensiveData.calendarEvents as { items?: unknown[]; _items?: unknown[] };
          calendarEventsResult = calendarData?.items || calendarData?._items || [];
        }
      } else {
        // No calendar events data found
      }

      // queryBlockedSlots can return only a subset of calendar events for large windows.
      // Use chunked queryBlockedSlots calls with empty serviceIds (events-only) to gather full range.
      try {
        const chunkedEvents: unknown[] = [];
        const rangeStart = new Date(params.startDate);
        const rangeEnd = new Date(params.endDate);
        const CHUNK_DAYS = 3;

        let chunkStart = new Date(rangeStart);
        while (chunkStart <= rangeEnd) {
          const chunkEnd = new Date(chunkStart);
          chunkEnd.setDate(chunkEnd.getDate() + CHUNK_DAYS);
          if (chunkEnd > rangeEnd) chunkEnd.setTime(rangeEnd.getTime());

          const chunkResponse = await callAdminApi('queryBlockedSlots', 'bookings', {
            serviceIds: [],
            startDate: chunkStart.toISOString(),
            endDate: chunkEnd.toISOString(),
          });

          const chunkCalendarRaw = (chunkResponse as { calendarEvents?: unknown })?.calendarEvents;
          const chunkItems = Array.isArray(chunkCalendarRaw)
            ? chunkCalendarRaw
            : ((chunkCalendarRaw as { items?: unknown[]; _items?: unknown[] } | undefined)?.items
              || (chunkCalendarRaw as { items?: unknown[]; _items?: unknown[] } | undefined)?._items
              || []);

          if (chunkItems.length) {
            chunkedEvents.push(...chunkItems);
          }

          const nextStart = new Date(chunkEnd);
          nextStart.setMilliseconds(nextStart.getMilliseconds() + 1);
          chunkStart = nextStart;
        }

        const seenEventKeys = new Set<string>();
        const mergedEvents: unknown[] = [];
        [...calendarEventsResult, ...chunkedEvents].forEach((eventRaw, idx) => {
          const eventObj = eventRaw as Record<string, unknown>;
          const eventId = (eventObj._id as string) || (eventObj.id as string) || `idx-${idx}`;
          const key = `${eventId}|${String(eventObj.type || '')}|${String(eventObj.title || '')}`;
          if (seenEventKeys.has(key)) return;
          seenEventKeys.add(key);
          mergedEvents.push(eventRaw);
        });

        calendarEventsResult = mergedEvents;

        console.log('[BlockedSlotsDebug] queryBlockedSlotsInternal:calendarEventsChunking', {
          initialEventsCount: Array.isArray(comprehensiveData.calendarEvents)
            ? comprehensiveData.calendarEvents.length
            : (((comprehensiveData.calendarEvents as { items?: unknown[]; _items?: unknown[] } | undefined)?.items
              || (comprehensiveData.calendarEvents as { items?: unknown[]; _items?: unknown[] } | undefined)?._items
              || []).length),
          chunkedEventsCount: chunkedEvents.length,
          mergedEventsCount: calendarEventsResult.length,
          chunkDays: CHUNK_DAYS,
        });
      } catch (error) {
        console.warn('[BlockedSlotsDebug] calendar events chunking fallback failed:', error);
      }

      // Event query payload can omit reliable time fields for SOME event
      // types — hydrating EVERY event unconditionally (previously ~1071 for a
      // full-year range) fires that many admin API calls just to re-fetch
      // data most events already have, and blows straight through Wix's rate
      // limit (a smaller concurrency-capped batch still hit the same wall,
      // since the limit is a sustained request-rate cap, not a concurrency
      // cap). Only hydrate events whose raw payload doesn't already parse to
      // a valid start time — the same field aliases processCalendarEvents
      // itself checks — which cuts the call volume down to just the events
      // that actually need it.
      const needsHydration = (eventRaw: unknown): boolean => {
        const eventObj = eventRaw as Record<string, unknown>;
        const rawStart = eventObj.start ?? eventObj.startDate ?? eventObj.from ?? eventObj.startTime;
        return !this.parseEventTime(rawStart);
      };

      const HYDRATE_BATCH_SIZE = 20;
      const eventsToHydrate = calendarEventsResult.filter(needsHydration);
      const hydratedById = new Map<string, unknown>();
      for (let i = 0; i < eventsToHydrate.length; i += HYDRATE_BATCH_SIZE) {
        const batch = eventsToHydrate.slice(i, i + HYDRATE_BATCH_SIZE);
        await Promise.all(
          batch.map(async (eventRaw) => {
            const eventObj = eventRaw as Record<string, unknown>;
            const eventId = (eventObj._id as string) || (eventObj.id as string);
            if (!eventId) return;

            try {
              const detailedResponse = await callAdminApi('getEvent', 'bookings', { id: eventId });
              const detailedEvent = (detailedResponse as { event?: Record<string, unknown> })?.event
                || (detailedResponse as Record<string, unknown>);

              if (!detailedEvent || typeof detailedEvent !== 'object') {
                return;
              }

              hydratedById.set(eventId, {
                ...eventObj,
                ...detailedEvent,
                _id: (detailedEvent._id as string) || eventId,
              });
            } catch {
              // Leave un-hydrated — falls back to the raw event below.
            }
          })
        );
      }

      console.log('[BlockedSlotsDebug] queryBlockedSlotsInternal:hydration', {
        totalEvents: calendarEventsResult.length,
        eventsNeedingHydration: eventsToHydrate.length,
        eventsHydrated: hydratedById.size,
      });

      calendarEventsResult = calendarEventsResult.map((eventRaw) => {
        const eventObj = eventRaw as Record<string, unknown>;
        const eventId = (eventObj._id as string) || (eventObj.id as string);
        if (eventId && hydratedById.has(eventId)) return hydratedById.get(eventId);
        return eventRaw;
      });

      const calendarEventSamples = calendarEventsResult.slice(0, 5).map((event, idx) => {
        const e = event as Record<string, unknown>;
        return {
          idx,
          id: e._id || e.id || null,
          type: e.type || null,
          title: e.title || null,
          keys: Object.keys(e),
          start: e.start ?? null,
          end: e.end ?? null,
          startDate: e.startDate ?? null,
          endDate: e.endDate ?? null,
          from: e.from ?? null,
          to: e.to ?? null,
          startTime: e.startTime ?? null,
          endTime: e.endTime ?? null,
        };
      });
      console.log('[BlockedSlotsDebug] queryBlockedSlotsInternal:calendarEventSamples:json', JSON.stringify(calendarEventSamples));

      // Do not make additional queryEvents calls here; backend may not support the same query shape.
      
      // Step 5: Process schedules from API query result (moved from handler)
      let schedulesResult: unknown[] = [];
      if (allSchedules) {
        // Check if it's already an array (direct items) or wrapped in object
        if (Array.isArray(allSchedules)) {
          schedulesResult = allSchedules;
        } else {
          // Check for both items and _items formats
          const schedulesData = allSchedules as { items?: unknown[]; _items?: unknown[] };
          schedulesResult = schedulesData?.items || schedulesData?._items || [];
        }
      } else {
        // No schedules data found from API
      }

      // querySchedules can return summary objects that omit blocked/time-off fields.
      // Enrich with getSchedule details so schedule-based blocks can be extracted reliably.
      const detailedSchedulesResult = await Promise.all(
        schedulesResult.map(async (scheduleRaw) => {
          const scheduleObj = scheduleRaw as { _id?: string; id?: string };
          const scheduleId = scheduleObj._id || scheduleObj.id;
          if (!scheduleId) return scheduleRaw;
          try {
            const detailed = await callAdminApi('getSchedule', 'bookings', { id: scheduleId });
            if (!detailed || typeof detailed !== 'object') return scheduleRaw;

            const detailedObj = detailed as {
              schedule?: Record<string, unknown>;
              _id?: string;
              id?: string;
            };

            const innerSchedule = detailedObj.schedule;
            if (innerSchedule && typeof innerSchedule === 'object') {
              return {
                ...scheduleRaw as Record<string, unknown>,
                ...innerSchedule,
                _id: (innerSchedule._id as string) || scheduleId,
              };
            }

            return {
              ...scheduleRaw as Record<string, unknown>,
              ...detailedObj,
              _id: detailedObj._id || scheduleId,
            };
          } catch {
            return scheduleRaw;
          }
        })
      );

      const schedulesWithBlocks = detailedSchedulesResult.filter((scheduleRaw) => {
        const scheduleObj = scheduleRaw as Record<string, unknown>;
        return Boolean(
          scheduleObj.timeOff
          || scheduleObj.timeOffPeriods
          || scheduleObj.unavailableIntervals
          || scheduleObj.exceptions
          || scheduleObj.blackoutPeriods
          || scheduleObj.blockedTime
        );
      });

      console.log('[BlockedSlotsDebug] queryBlockedSlotsInternal:schedules', {
        schedulesSummaryCount: schedulesResult.length,
        schedulesDetailedCount: detailedSchedulesResult.length,
        schedulesWithBlocksCount: schedulesWithBlocks.length,
      });
      
      // Use the processed data
      const resources = resourcesResult as Resource[];
      const staffMembers = staffMembersResult as StaffMember[];
      const schedules = detailedSchedulesResult as Schedule[];

      const resourceMap = new Map<string, Resource>();
      resources.forEach(resource => {
        if (resource._id) resourceMap.set(resource._id, resource);
      });

      const staffMap = new Map<string, StaffMember>();
      staffMembers.forEach(staff => {
        if (staff._id) staffMap.set(staff._id, staff);
        if (staff.resourceId) staffMap.set(staff.resourceId, staff);
      });

      const scheduleMap = new Map<string, Schedule>();
      schedules.forEach(schedule => {
        if (schedule._id) scheduleMap.set(schedule._id, schedule);
      });

      // Process blocked slots from availability data (now directly from items)
      const blockedSlots = this.processBlockedSlots(availabilityData, resourceMap, staffMap, scheduleMap);

      // Process calendar events
      const calendarData = calendarEventsResult as unknown[];
      const calendarEvents = this.processCalendarEvents(calendarData, resourceMap, staffMap, scheduleMap);

      // Process schedule-based blocked time
      const startDateObj = new Date(params.startDate);
      const endDateObj = new Date(params.endDate);
      const scheduleBlocked = this.processSchedulesForBlockedTime(schedules, resourceMap, staffMap, startDateObj, endDateObj);

      // Combine all blocked time sources
      const allBlockedItems = [
        ...blockedSlots,
        ...calendarEvents,
        ...scheduleBlocked
      ];

      console.log('[BlockedSlotsDebug] queryBlockedSlotsInternal:processed', {
        availabilityBlocked: blockedSlots.length,
        calendarEventsBlocked: calendarEvents.length,
        schedulesBlocked: scheduleBlocked.length,
        totalBlocked: allBlockedItems.length,
      });

      return {
        items: allBlockedItems,
        resources: resourcesResult,
        staff: staffMembersResult,
        schedules: schedulesResult,
        metadata: {
          total: allBlockedItems.length,
          sources: {
            availability: blockedSlots.length,
            calendarEvents: calendarEvents.length,
            schedules: scheduleBlocked.length
          }
        }
      };

    } catch (error) {
      throw error;
    }
  }

  async updateBookingWithAvailabilityCheck(booking: Booking, options?: { adminOverride?: boolean; userRole?: 'owner' | 'admin' | 'manager' | 'user' }): Promise<{
    booking?: Booking;
    warning?: string;
    error?: string;
    availabilityStatus: 'available' | 'unavailable' | 'admin_override';
  }> {
    try {
      // Get the booking to retrieve its revision (same method as cancelBooking)
      const bookingResult = await callAdminApi('getBooking', 'bookings', { 
        filter: { _id: booking.id }
      });
      console.log('🔍 Booking result for revision (availability check):', bookingResult);
      
      // Extract revision, scheduleId, and resourceId from the booking data (same structure as cancelBooking uses)
      let revision: number | undefined;
      let existingScheduleId: string | undefined;
      let existingResourceId: string | undefined;
      const resultWithExtended = bookingResult as { extendedBookings?: Array<{ booking?: { revision?: string | number; bookedEntity?: { scheduleId?: string; slot?: { scheduleId?: string; resource?: { _id?: string } } } } }> };
      if (Array.isArray(resultWithExtended?.extendedBookings) && resultWithExtended.extendedBookings.length > 0) {
        const bookingData = resultWithExtended.extendedBookings[0]?.booking;
        const rev = bookingData?.revision;
        revision = typeof rev === 'string' ? parseInt(rev, 10) : rev;
        existingScheduleId = bookingData?.bookedEntity?.scheduleId;
        existingResourceId = bookingData?.bookedEntity?.slot?.resource?._id;
      }
      
      console.log('📌 Extracted revision (availability check):', revision);
      console.log('📌 Extracted scheduleId (availability check):', existingScheduleId);
      console.log('📌 Extracted resourceId (availability check):', existingResourceId);
      
      if (!revision) {
        console.error('❌ No revision found in booking result:', bookingResult);
        throw new Error('Unable to get booking revision for update');
      }
      
      // Get real service ID from Wix services based on service type
      let serviceId: string | null = null;
      
      try {
        const serviceTypes = await this.serviceTypesApi.getServiceTypes();
        const matchingService = serviceTypes.find(service => 
          service.type === booking.serviceType
        );
        
        if (matchingService && matchingService.serviceId) {
          serviceId = matchingService.serviceId;
        } else {
          // If no exact match, use the first available service
          if (serviceTypes.length > 0 && serviceTypes[0].serviceId) {
            serviceId = serviceTypes[0].serviceId;
          } else {
            throw new Error('No real services found in Wix');
          }
        }
      } catch (error) {
        throw new Error(`Failed to query services: ${error}`);
      }

      if (!serviceId) {
        throw new Error('Unable to determine real service ID for booking update');
      }

      // Get real availability data for the booking time
      let availabilityStatus: 'available' | 'unavailable' | 'admin_override' = 'unavailable';
      let warningMessage: string | null = null;
      let selectedSlotData: { slot?: Record<string, unknown>; bookable?: boolean } | null = null;

      try {
        const startTime = booking.startTime instanceof Date ? booking.startTime : new Date(booking.startTime);
        const endTime = booking.endTime instanceof Date ? booking.endTime : new Date(booking.endTime);
        
        // Expand the search range to find available slots around the requested time
        const searchStartTime = new Date(startTime);
        searchStartTime.setHours(0, 0, 0, 0); // Start of day
        
        const searchEndTime = new Date(endTime);
        searchEndTime.setHours(23, 59, 59, 999); // End of day
        
        // Use proper query structure based on Wix SDK documentation
        const availabilityQuery = {
          filter: {
            serviceId: serviceId,
            startDate: searchStartTime.toISOString(),
            endDate: searchEndTime.toISOString()
          },
          timezone: 'America/Los_Angeles',
          bookable: true
        };
        
        const availabilityResult = await queryAvailabilityTimeSlots(availabilityQuery);

        // Look for available appointment slots
        const availabilityEntries = (availabilityResult as {
          availabilityEntries?: Array<{
            slot?: {
              serviceId?: string;
              scheduleId?: string;
              startDate?: string;
              endDate?: string;
              resource?: {
                name?: string;
                _id?: string;
              };
              location?: {
                locationType?: string;
              };
            };
            bookable?: boolean;
          }>
        })?.availabilityEntries || [];

        // Determine if this caller is an admin/owner/manager/salesManager (they get more flexible booking options)
        const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));

        // Try to find a slot that matches our requested time EXACTLY
        const exactMatchingSlot = availabilityEntries.find(entry => {
          if (!entry.slot?.startDate || !entry.slot?.endDate) return false;
          
          const slotStart = new Date(entry.slot.startDate);
          const slotEnd = new Date(entry.slot.endDate);
          
          // Check for exact time match (within 1 minute tolerance)
          return Math.abs(slotStart.getTime() - startTime.getTime()) < 60000 && 
                 Math.abs(slotEnd.getTime() - endTime.getTime()) < 60000;
        });

        // Select the exact matching slot if found
        let selectedSlot = exactMatchingSlot;
        
        // If no exact match found, allow admins to use a containing-slot override (minute-by-minute)
        if (!selectedSlot) {
          if (isAdminUser) {
            const containingSlot = availabilityEntries.find(entry => {
              if (!entry.slot?.startDate || !entry.slot?.endDate) return false;
              const slotStart = new Date(entry.slot.startDate);
              const slotEnd = new Date(entry.slot.endDate);
              // Check if the slot fully contains the requested interval
              return slotStart <= startTime && slotEnd >= endTime;
            });

            if (containingSlot) {
              // Create an overridden slot that preserves metadata but uses requested start/end
              selectedSlot = {
                slot: {
                  ...(containingSlot.slot || {}),
                  startDate: startTime.toISOString(),
                  endDate: endTime.toISOString()
                },
                bookable: containingSlot.bookable
              } as typeof availabilityEntries[number];

              availabilityStatus = containingSlot.bookable ? 'available' : 'admin_override';
            } else {
              // Admins: no containing slot found -> cannot proceed
              throw new Error('No containing slot available for the requested admin override time');
            }
          } else {
            // Non-admin and no exact match -> cannot proceed
            throw new Error('Requested time does not match any exact available slot');
          }
        }
        
        if (selectedSlot) {
          selectedSlotData = selectedSlot;
          
          if (selectedSlot.bookable) {
            availabilityStatus = 'available';
          } else {
            availabilityStatus = 'admin_override';
          }
        } else {
          throw new Error('No available appointment slots found from Wix API for update');
        }
      } catch {
        
        // If availability check fails, we cannot proceed without real data
        const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
        
        if (isAdminUser) {
          throw new Error('Unable to verify slot availability for appointment booking update. Cannot update booking without valid Wix availability data.');
        } else {
          throw new Error('Unable to verify slot availability for appointment booking update. Please try again.');
        }
      }

      // Transform booking data to Wix API format
      const startDateTime = booking.startTime instanceof Date ? booking.startTime : new Date(booking.startTime);
      const endDateTime = booking.endTime instanceof Date ? booking.endTime : new Date(booking.endTime);
      
      // Build additional fields with conditional admin warning
      const additionalFields = [
        { id: this.generateGUID(), key: 'notes', value: booking.notes || '' },
        { id: this.generateGUID(), key: 'location', value: booking.location || '' },
        { id: this.generateGUID(), key: 'price', value: booking.price?.toString() || '0' },
        { id: this.generateGUID(), key: 'duration', value: booking.duration?.toString() || '0' },
        { id: this.generateGUID(), key: 'resource', value: booking.resource || '' },
        { id: this.generateGUID(), key: 'staff', value: booking.staff || '' },
        { id: this.generateGUID(), key: 'availabilityStatus', value: availabilityStatus }
      ];

      // Add admin warning if present
      if (warningMessage) {
        additionalFields.push({ id: this.generateGUID(), key: 'adminWarning', value: warningMessage });
      }

      // Only use real contactId - no fallbacks allowed
      if (!booking.contactId || booking.contactId.trim() === '') {
        throw new Error('Valid contactId is required for booking update - no generated IDs allowed');
      }

      // Build the slot object for rescheduleBooking
      // If we have selectedSlotData from availability query, use it (it already has scheduleId)
      // Otherwise, construct one with the existing scheduleId from the booking
      const slot: Record<string, unknown> = selectedSlotData?.slot || {
        serviceId: serviceId,
        scheduleId: existingScheduleId, // Required by Wix when sessionId is not set
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        timezone: 'America/Los_Angeles',
        location: {
          locationType: 'CUSTOM',
          formattedAddress: booking.location || ''
        }
      };

      // Only include resource if we have an existingResourceId and didn't get slot from availability
      if (!selectedSlotData?.slot && existingResourceId) {
        slot.resource = { id: existingResourceId };
      }

      // Note: additionalFields (notes, price, duration, resource, staff) from the booking form
      // cannot be updated after creation - they are part of the form submission and are read-only.
      // Only the slot/time/resource can be updated via rescheduleBooking.

      // Step 1: Reschedule the booking (updates slot/time)
      // Build options object with revision and flow control settings based on admin status
      const isAdminUser = ['owner', 'admin', 'manager', 'salesManager'].includes(String(options?.userRole));
      const rescheduleResult = await callAdminApi('updateBooking', 'bookings', {
        id: booking.id,
        slot: slot,
        options: {
          revision: revision,
          ...(isAdminUser && availabilityStatus === 'admin_override' && {
            flowControlSettings: {
              ignoreBookingWindow: true,
              skipAvailabilityValidation: true,
              skipBusinessConfirmation: true
            }
          })
        }
      });

      // Note: additionalFields (notes, price, duration, resource, staff) from the booking form
      // cannot be updated after creation - they are part of the form submission and are read-only.
      // Only the slot/time/resource can be updated via rescheduleBooking.

      const updatedBooking = rescheduleResult as unknown as Booking;

      return {
        booking: updatedBooking,
        warning: warningMessage || undefined,
        availabilityStatus
      };
    } catch (error) {
      return {
        error: `Failed to update booking: ${error instanceof Error ? error.message : 'Unknown error'}`,
        availabilityStatus: 'unavailable'
      };
    }
  }

  async deleteBookingWithOptions(id: string, _options?: { userRole?: 'owner' | 'admin' | 'manager' | 'user' }): Promise<boolean> {
    try {
      // For admin users, we might want to add special handling in the future
      // For now, all users can delete bookings they have access to
      await callAdminApi('deleteBooking', 'bookings', { id });
      
      // Notify all tabs that a booking was deleted
      DataStorageService.notifyBookingChanged();
      
      return true;
    } catch {
      return false;
    }
  }
}